// DashboardPublicoPage.js — Dashboard ejecutivo de programa (/dashboard/:token).
//
// Acceso por correo (lista blanca). Rediseño sobre el modelo ejecutivo de
// OsteoExperts (docs/modelodasboard.html): barra de filtros pegajosa que manda
// sobre TODAS las pestañas, KPIs con tono, tendencia de registros, cobertura por
// segmento, journey por módulo, satisfacción, listado de estudiantes y calidad
// de datos. Tema claro/oscuro con los tokens de components/dashboard/dashboardUI.
//
// Usa axios propio (sin interceptor → no redirige a /login).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ThemeProvider } from 'styled-components';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  ComposedChart, Bar as RBar, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { API_BASE_URL, BACKEND_BASE } from '../services/api';
import { toDateUTC, fmtFechaBogota } from '../utils/fecha';
import { normalizarPestanas, normalizarPanelesJourney, normalizarMetas, valorSegmento, etiquetaSegmento, claveSegmento, SIN_DATO, textoEtiqueta } from '../utils/dashboardPestanas';
import {
  useTemaDashboard, useChartTheme, GlobalDash,
  nf, df, pct, tonoPorPct, bajarCSV,
  Wrap, Top, Brand, BrandName, BrandSub, TopTools, CutBox, ThemeBtn, Container,
  FilterBar, FilterSummary, Field, Input, Select, Btn, Ghost,
  Tabs, Tab, Heading, Eyebrow, H2, Note, Actions,
  Panel, PanelHead, PanelBody, Grid2, Empty,
  KpiGrid, KpiCard, Bars, BarRow, Track, FillA, FillB, BarVal, Legend,
  MiniGrid, Mini, Cubes, Cube, CardGrid, CardBtn, Bar, Fill,
  Chips, Chip, Dot, TableWrap, Table, Th, Td, TableTools, Badge, Pager,
  AuditHero, Calor, BarrasHora, SurveyGrid, SurveyCard, AspectGrid, Aspect, Progress, Sentiment, BarBtn, Comentario,
  Center, Gate, GateInput, Primary, Err, Foot,
} from '../components/dashboard/dashboardUI';
import EventoCertificacion from '../components/dashboard/EventoCertificacion';

const api = axios.create({ baseURL: API_BASE_URL });

const resolveLogo = (l) => (!l ? null : (String(l).startsWith('http') ? l : `${BACKEND_BASE}/${l}`));
const fmtFecha = (s) => (s ? fmtFechaBogota(s) : '—');
// "AAAA-MM-DD" en hora Colombia: así los rangos del filtro coinciden con la
// fecha que ve el usuario en la tabla (la BD guarda UTC).
const soloFecha = (s) => {
  const d = toDateUTC(s);
  return d ? d.toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }) : '';
};

// Segmentos de puntuación (torta)
const BUCKETS = [
  { key: 'Completado (100%)', c: 'ok', test: (s) => s >= 100 },
  { key: 'Avanzado (50-99%)', c: 'info', test: (s) => s >= 50 && s < 100 },
  { key: 'Inicial (1-49%)', c: 'warn', test: (s) => s > 0 && s < 50 },
  { key: 'Sin iniciar (0%)', c: 'bad', test: (s) => s <= 0 },
];
const bucketOf = (s) => (BUCKETS.find(b => b.test(s)) || BUCKETS[3]).key;

// Las pestañas (cuáles, orden, títulos, segmentos de Cobertura) vienen de la
// configuración del dashboard: utils/dashboardPestanas.js.

// Estado de un correo tal como lo manda el backend (DashboardHelper::comunicaciones).
const ETQ_CORREO = {
  entregado: 'Entregado', enviado: 'Enviado', rebote: 'Rebotó', queja: 'Reclamó (spam)',
  fallido: 'Falló', suprimido: 'No enviado (baja o rebote previo)',
};
const LISTAS_CORREO = [
  { key: 'todos', label: 'Todos' },
  { key: 'abrieron', label: 'Abrieron' },
  { key: 'no_abrieron', label: 'No abrieron' },
  { key: 'rebotes', label: 'Rebotaron' },
  { key: 'quejas', label: 'Reclamaron' },
  { key: 'bajas', label: 'Se dieron de baja' },
  { key: 'clic', label: 'Hicieron clic' },
];

// Tipos de correo en Comunicaciones (tipo que manda DashboardHelper::comunicaciones).
const TIPOS_CORREO = [
  { key: '', label: 'Todos' },
  { key: 'flujo', label: 'Flujo automático' },
  { key: 'comunicado', label: 'Comunicados' },
  { key: 'registro', label: 'Registro' },
  { key: 'recuperar', label: 'Restablecer contraseña' },
];

const FILTROS_VACIOS = {
  q: '', pais: '', especialidad: '', convenio: '', subrol: '', estado: '',
  regDesde: '', regHasta: '', actividad: 'all',
  // Cuándo obtuvo el certificado (distinto de cuándo se registró).
  certDesde: '', certHasta: '',
  // Campos del registro que el tablero deja filtrar (p. ej. visitador): campo → valor.
  perfil: {},
};

// ── Agregados ───────────────────────────────────────────────────────────────
// Los campos de texto libre (p. ej. ciudad) traen variantes de la misma respuesta:
// «Bogotá», «Bogota», « bogotá ». Se agrupan sin tildes ni mayúsculas (claveSegmento)
// y la barra lleva la forma más escrita.
const agrupar = (rows, campo, leer = null) => {
  const m = new Map();
  rows.forEach(u => {
    const bruto = (leer ? leer(u, campo) : u[campo]) || 'Sin dato';
    const k = claveSegmento(bruto) || 'sin dato';
    const v = m.get(k) || { name: bruto, registrados: 0, certificados: 0, avance: 0, formas: {} };
    const forma = String(bruto).trim();
    v.formas[forma] = (v.formas[forma] || 0) + 1;
    v.registrados++; v.certificados += u.cumple ? 1 : 0; v.avance += u.avance || 0;
    m.set(k, v);
  });
  return [...m.values()]
    .map(({ formas, ...v }) => ({
      ...v,
      name: Object.entries(formas).sort((a, b) => b[1] - a[1])[0][0],
      tasa: v.certificados / v.registrados,
      avance: v.avance / v.registrados,
    }))
    .sort((a, b) => b.registrados - a.registrados);
};

const contar = (rows, campo) => {
  const m = new Map();
  rows.forEach(r => { const k = r[campo] || 'Sin dato'; m.set(k, (m.get(k) || 0) + 1); });
  return [...m].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
};

const metricas = (rows) => {
  const n = rows.length;
  const cert = rows.filter(r => r.cumple).length;
  const prog = rows.filter(r => r.estado === 'En progreso').length;
  const sin = rows.filter(r => r.estado === 'Sin iniciar').length;
  const avance = n ? rows.reduce((a, r) => a + (r.avance || 0), 0) / n / 100 : 0;
  return { n, cert, prog, sin, avance, tasa: n ? cert / n : 0 };
};

// ── Bloques reutilizables ───────────────────────────────────────────────────
const BarrasResultado = ({ rows, limite = 15, tema }) => {
  if (!rows.length) return <Empty>Sin datos para los filtros seleccionados.</Empty>;
  const max = Math.max(...rows.slice(0, limite).map(r => r.registrados), 1);
  return (
    <>
      <Bars>
        {rows.slice(0, limite).map(r => (
          <BarRow key={r.name}>
            <span title={r.name}>{r.name}</span>
            <Track>
              <FillA style={{ width: `${r.registrados / max * 100}%` }} />
              <FillB style={{ width: `${r.certificados / max * 100}%` }} />
            </Track>
            <BarVal>{nf.format(r.registrados)} / {nf.format(r.certificados)}<em>{pct(r.tasa)} certificados</em></BarVal>
          </BarRow>
        ))}
      </Bars>
      <Legend>
        <span><i style={{ background: tema.serieA }} />Registrados</span>
        <span><i style={{ background: tema.serieB }} />Certificados</span>
      </Legend>
    </>
  );
};

const BarrasSimples = ({ rows, limite = 12, color }) => {
  if (!rows.length) return <Empty>Sin datos.</Empty>;
  const max = Math.max(...rows.slice(0, limite).map(r => r.value), 1);
  return (
    <Bars>
      {rows.slice(0, limite).map(r => (
        <BarRow key={r.name}>
          <span title={r.name}>{r.name}</span>
          <Track><FillA style={{ width: `${r.value / max * 100}%`, background: color }} /></Track>
          <BarVal>{nf.format(r.value)}</BarVal>
        </BarRow>
      ))}
    </Bars>
  );
};

const Tendencia = ({ rows, acumulado = 'Registros acumulados', nuevos = 'Nuevos registros', vacio = 'Los estudiantes filtrados no tienen fecha de registro.' }) => {
  const ct = useChartTheme();
  if (!rows.length) return <Empty>{vacio}</Empty>;
  return (
    <>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid stroke={ct.grid} vertical={false} />
            <XAxis dataKey="mes" tick={{ fill: ct.axis, fontSize: 11 }} tickLine={false} axisLine={{ stroke: ct.grid }} />
            <YAxis tick={{ fill: ct.axis, fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={ct.tooltip} labelStyle={{ color: ct.axis }} cursor={{ fill: ct.grid, opacity: 0.35 }} />
            <RBar dataKey="acumulado" name={acumulado} fill={ct.serieA} radius={[5, 5, 0, 0]} maxBarSize={44} />
            <Line type="monotone" dataKey="nuevos" name={nuevos} stroke={ct.serieC} strokeWidth={3} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

// ── Página ──────────────────────────────────────────────────────────────────
const DashboardPublicoPage = () => {
  const { token } = useParams();
  const [tema, modo, alternarTema] = useTemaDashboard();

  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState('');
  const [pasoCodigo, setPasoCodigo] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [aviso, setAviso] = useState('');
  const [accessToken, setAccessToken] = useState(() => sessionStorage.getItem(`dash_at_${token}`) || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [pestana, setPestana] = useState('');
  const [f, setF] = useState(FILTROS_VACIOS);
  // Cada tendencia se mira por día o por mes según lo que se quiera ver.
  const [granReg, setGranReg] = useState('mes');
  const [granCert, setGranCert] = useState('dia');
  const setFiltro = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const [focus, setFocus] = useState(null);        // {tipo:'mod'|'eval'|'req', key, titulo}
  const [segFilter, setSegFilter] = useState(null); // bucket seleccionado en la torta
  const [page, setPage] = useState(0);
  const [orden, setOrden] = useState({ campo: 'nombre', dir: 1 });
  const [verRespuestas, setVerRespuestas] = useState(false);
  const [encPage, setEncPage] = useState(0);
  const [comentFiltro, setComentFiltro] = useState(null);   // {tipo:'tema'|'sentimiento', valor}
  const [comentMax, setComentMax] = useState(20);
  const [abiertas, setAbiertas] = useState({});
  const alternar = (k) => setAbiertas(prev => ({ ...prev, [k]: !prev[k] }));
  const [compara, setCompara] = useState({ desde: '', hasta: '' });

  // Las etiquetas pueden venir traducidas ({es, pt}); en la tabla se muestran como texto.
  const perfilMeta = useMemo(() => (data?.perfil_meta || []).map(c => ({ ...c, etiqueta: textoEtiqueta(c.etiqueta) })), [data]);
  // «Certificación del evento» sólo aparece si el dashboard tiene un evento configurado.
  const pestanas = useMemo(() => normalizarPestanas(data?.pestanas)
    .filter(p => p.visible && (p.tipo !== 'evento' || data?.evento)), [data]);
  // Si la pestaña elegida ya no está (o es la primera carga), se abre la primera visible.
  useEffect(() => {
    if (pestanas.length && !pestanas.some(p => p.id === pestana)) setPestana(pestanas[0].id);
  }, [pestanas, pestana]);
  const pestanaActual = pestanas.find(p => p.id === pestana) || null;
  const tipoActual = pestanaActual?.tipo || '';
  // Lista de contacto de las pestañas de cobertura: orden y página (se reinicia al cambiar de pestaña).
  const [contactoOrden, setContactoOrden] = useState({ campo: 'dias_inactividad', dir: -1, page: 0 });
  useEffect(() => { setContactoOrden({ campo: 'dias_inactividad', dir: -1, page: 0 }); }, [pestana]);
  const certMeta = useMemo(() => data?.certificados_meta || [], [data]);

  useEffect(() => {
    api.get(`/dashboard-publico.php?token=${encodeURIComponent(token)}`)
      .then(res => { if (res.data?.ok) setInfo(res.data); else setNotFound(true); })
      .catch(() => setNotFound(true));
  }, [token]);

  const loadData = useCallback((at) => {
    setLoading(true); setError('');
    api.get('/dashboard-data.php', { headers: { Authorization: `Bearer ${at}` } })
      .then(res => { if (res.data?.ok) setData(res.data); else setError(res.data?.message || 'Error'); })
      .catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          sessionStorage.removeItem(`dash_at_${token}`); setAccessToken(''); setData(null); setPasoCodigo(false);
          setError('Tu acceso expiró. Vuelve a ingresar tu correo.');
        } else setError('No se pudo cargar el dashboard.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { if (accessToken) loadData(accessToken); }, [accessToken, loadData]);
  useEffect(() => { setSegFilter(null); }, [focus]);
  useEffect(() => { setPage(0); }, [f, focus, segFilter]);

  // Acceso en dos pasos: el correo recibe un código de 6 dígitos y con él se entra.
  const submitEmail = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setError(''); setAviso('');
    api.post('/dashboard-acceso.php', { token, email: email.trim() })
      .then(res => {
        if (res.data?.ok) { setPasoCodigo(true); setCodigo(''); setAviso(res.data.message || ''); }
        else setError(res.data?.message || 'No se pudo enviar el código');
      })
      .catch(err => setError(err.response?.data?.message || 'No se pudo enviar el código.'))
      .finally(() => setLoading(false));
  };

  const submitCodigo = (e) => {
    e.preventDefault();
    if (codigo.replace(/\D/g, '').length !== 6) { setError('El código tiene 6 dígitos.'); return; }
    setLoading(true); setError('');
    api.post('/dashboard-acceso.php', { token, email: email.trim(), codigo })
      .then(res => {
        if (res.data?.ok) { sessionStorage.setItem(`dash_at_${token}`, res.data.access_token); setAccessToken(res.data.access_token); }
        else setError(res.data?.message || 'Código incorrecto');
      })
      .catch(err => setError(err.response?.data?.message || 'Código incorrecto.'))
      .finally(() => setLoading(false));
  };

  const estudiantes = useMemo(() => data?.estudiantes || [], [data]);
  const reqMeta = useMemo(() => data?.requisitos_meta || [], [data]);
  const modMeta = useMemo(() => data?.modulos_meta || [], [data]);
  const evalMeta = useMemo(() => data?.evaluaciones_meta || [], [data]);

  // Opciones de los selectores, sacadas de los propios datos.
  const hayFechasCert = useMemo(
    () => estudiantes.some(e => e.certs_fechas && Object.keys(e.certs_fechas).length),
    [estudiantes]
  );

  const opciones = useMemo(() => {
    const uniq = (campo) => [...new Set(estudiantes.map(e => e[campo]).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), 'es'));
    const delPerfil = (campo) => [...new Set(estudiantes.map(e => e.perfil?.[campo]).filter(Boolean).map(String))]
      .sort((a, b) => a.localeCompare(b, 'es'));
    return {
      pais: uniq('pais'), especialidad: uniq('especialidad'),
      convenio: uniq('convenio'), subrol: uniq('subrol'),
      perfil: Object.fromEntries((data?.filtros_barra || []).map(c => [c, delPerfil(c)])),
    };
  }, [estudiantes, data]);

  // ── Filtro maestro: manda sobre todas las pestañas ──
  const filtrados = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    const corte = f.actividad === 'all' ? null : Date.now() - Number(f.actividad) * 86400000;
    return estudiantes.filter(e => {
      if (q && ![e.nombre, e.email, e.dni, e.telefono].join(' ').toLowerCase().includes(q)) return false;
      if (f.pais && (e.pais || '') !== f.pais) return false;
      if (f.especialidad && (e.especialidad || '') !== f.especialidad) return false;
      if (f.convenio && (e.convenio || '') !== f.convenio) return false;
      if (f.subrol && (e.subrol || '') !== f.subrol) return false;
      if (f.estado && e.estado !== f.estado) return false;
      for (const [campo, valor] of Object.entries(f.perfil || {})) {
        if (valor && String(e.perfil?.[campo] ?? '') !== valor) return false;
      }
      if (f.regDesde && soloFecha(e.fecha_registro) < f.regDesde) return false;
      if (f.regHasta && soloFecha(e.fecha_registro) > f.regHasta) return false;
      if (f.certDesde || f.certHasta) {
        const fechas = Object.values(e.certs_fechas || {}).map(soloFecha).filter(Boolean);
        const dentro = fechas.some(d => (!f.certDesde || d >= f.certDesde) && (!f.certHasta || d <= f.certHasta));
        if (!dentro) return false;
      }
      if (corte) {
        const d = toDateUTC(e.ultima_actividad);
        if (!d || d.getTime() < corte) return false;
      }
      return true;
    });
  }, [estudiantes, f]);

  const filtrosActivos = useMemo(
    () => Object.keys(FILTROS_VACIOS).filter(k => k !== 'perfil' && f[k] !== FILTROS_VACIOS[k]).length
        + Object.values(f.perfil || {}).filter(Boolean).length,
    [f]
  );

  // Puntuación de un estudiante según el contexto elegido en Journey.
  const scoreOf = useCallback((e) => {
    if (focus?.tipo === 'mod') return e.mod?.[focus.key]?.pct ?? 0;
    if (focus?.tipo === 'req') return e.req?.[focus.key] ? 100 : 0;
    if (focus?.tipo === 'eval') return e.eval?.[focus.key]?.pct ?? 0;
    return e.avance ?? 0;
  }, [focus]);

  const pieData = useMemo(() => {
    const c = {};
    filtrados.forEach(e => { const b = bucketOf(scoreOf(e)); c[b] = (c[b] || 0) + 1; });
    return BUCKETS.filter(b => c[b.key]).map(b => ({ name: b.key, value: c[b.key], color: tema[b.c] }));
  }, [filtrados, scoreOf, tema]);

  // Lo que ven las tablas: filtros + segmento de la torta.
  const visibles = useMemo(
    () => (segFilter ? filtrados.filter(e => bucketOf(scoreOf(e)) === segFilter) : filtrados),
    [filtrados, segFilter, scoreOf]
  );

  const m = useMemo(() => metricas(filtrados), [filtrados]);

  // NPS del programa (la encuesta no se puede filtrar por estudiante).
  const nps = useMemo(() => {
    const pregs = data?.encuesta?.preguntas || [];
    const res = data?.encuesta?.resumen?.por_pregunta || {};
    const p = pregs.find(x => x.tipo === 'nps');
    return p ? res[p.id] || null : null;
  }, [data]);

  // Satisfacción: todo se calcula sobre las respuestas de los estudiantes filtrados,
  // como en el modelo. Cada tipo de pregunta aporta lo suyo: NPS y matriz a las
  // tarjetas y a «Satisfacción por aspecto», las de opción a barras y las abiertas a
  // temas y sentimiento (clasificados en el servidor).
  const encuesta = useMemo(() => {
    const enc = data?.encuesta;
    if (!enc) return null;
    const todas = enc.respuestas || [];
    const filtrando = filtrados.length !== (data?.estudiantes || []).length;
    const correos = new Set(filtrados.map(e => (e.email || '').toLowerCase()));
    const resp = filtrando ? todas.filter(r => correos.has((r.email || '').toLowerCase())) : todas;
    const num = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
    const contar = (vals) => {
      const mapa = new Map();
      vals.forEach(v => mapa.set(v, (mapa.get(v) || 0) + 1));
      return [...mapa].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    };
    const maxDe = (p) => (p.tipo === 'rating' ? 5 : p.max || 5);
    const aspecto = (texto, clave, p) => {
      const vals = resp.map(r => num(r.respuestas?.[clave])).filter(x => x !== null);
      const max = maxDe(p);
      const n = vals.length;
      return {
        texto, max, n, vals,
        prom: n ? vals.reduce((a, x) => a + x, 0) / n : null,
        sat: n ? vals.filter(x => x >= max - 1).length / n : null,
      };
    };

    const tarjetas = [];
    const aspectos = [];
    const opciones = [];
    const abiertas = [];
    (enc.preguntas || []).forEach(p => {
      const titulo = p.titulo || p.texto;
      if (p.tipo === 'nps') {
        const vals = resp.map(r => num(r.respuestas?.[p.id])).filter(x => x !== null);
        const n = vals.length;
        const indice = n ? Math.round(((vals.filter(x => x >= 9).length - vals.filter(x => x <= 6).length) / n) * 100) : null;
        tarjetas.push({
          id: p.id, titulo,
          valor: n ? `${df.format(vals.reduce((a, x) => a + x, 0) / n)}/10` : '—',
          sub: n ? `NPS ${indice > 0 ? '+' : ''}${indice} · ${nf.format(n)} respuestas` : 'Sin respuestas',
        });
      } else if (p.tipo === 'matriz') {
        const filas = (p.filas || []).map(fl => aspecto(fl.texto, `${p.id}__${fl.id}`, p));
        aspectos.push(...filas);
        const todos = filas.flatMap(a => a.vals);
        const max = maxDe(p);
        tarjetas.push({
          id: p.id, titulo,
          valor: todos.length ? pct(todos.filter(x => x >= max - 1).length / todos.length) : '—',
          sub: `eSAT · satisfechos (${max - 1} o ${max})`,
        });
      } else if (p.tipo === 'escala' || p.tipo === 'rating') {
        const a = aspecto(titulo, p.id, p);
        aspectos.push(a);
        tarjetas.push({
          id: p.id, titulo,
          valor: a.n ? `${df.format(a.prom)}/${a.max}` : '—',
          sub: a.n ? `${pct(a.sat)} satisfechos` : 'Sin respuestas',
        });
      } else if (p.tipo === 'opcion') {
        const vals = resp.map(r => {
          const v = r.respuestas?.[p.id];
          return v === 'Otro' && r.respuestas?.[`${p.id}__otro`] ? 'Otro' : v;
        }).filter(v => typeof v === 'string' && v.trim());
        const filas = contar(vals);
        opciones.push({ id: p.id, titulo, filas, n: vals.length });
        tarjetas.push({
          id: p.id, titulo, largo: true,
          valor: filas[0]?.name || 'Sin respuestas',
          sub: filas[0] ? `${nf.format(filas[0].value)} de ${nf.format(vals.length)} respuestas` : '',
        });
      } else if (p.tipo === 'abierta') {
        const comentarios = resp
          .map(r => ({ texto: String(r.respuestas?.[p.id] ?? '').trim(), a: r.analisis?.[p.id] || null, nombre: r.nombre, fecha: r.fecha }))
          .filter(c => c.texto);
        const temas = contar(comentarios.filter(c => c.a && c.a.tema !== 'Sin comentario sustantivo').map(c => c.a.tema));
        abiertas.push({ id: p.id, titulo, comentarios, temas });
        tarjetas.push({
          id: p.id, titulo, largo: true,
          valor: temas[0]?.name || (comentarios.length ? `${nf.format(comentarios.length)} comentarios` : 'Sin respuestas'),
          sub: `${nf.format(comentarios.length)} comentarios`,
        });
      }
    });

    const clasificados = abiertas.flatMap(x => x.comentarios.filter(c => c.a));
    const sentimientos = ['Positivo', 'Neutral', 'Negativo'].map(name => ({
      name, value: clasificados.filter(c => c.a.sentimiento === name).length,
    }));
    return { resp, total: todas.length, filtrando, tarjetas, aspectos, opciones, abiertas, sentimientos, nClasificados: clasificados.length };
  }, [data, filtrados]);

  // Columnas de la tabla y del CSV: una por frase de las matrices y, en las abiertas,
  // también su tema y sentimiento.
  const columnasEncuesta = useMemo(() => {
    const cols = [];
    (data?.encuesta?.preguntas || []).forEach(p => {
      const titulo = p.titulo || p.texto;
      if (p.tipo === 'matriz') {
        (p.filas || []).forEach(fl => cols.push({ key: `${p.id}__${fl.id}`, titulo: fl.texto, valor: r => r.respuestas?.[`${p.id}__${fl.id}`], sufijo: `/${p.max || 5}` }));
      } else {
        const sufijo = p.tipo === 'nps' ? '/10' : p.tipo === 'rating' ? '/5' : p.tipo === 'escala' ? `/${p.max || 5}` : '';
        cols.push({ key: p.id, titulo, largo: p.tipo === 'abierta', valor: r => {
          const v = r.respuestas?.[p.id];
          return v === 'Otro' && r.respuestas?.[`${p.id}__otro`] ? `Otro: ${r.respuestas[`${p.id}__otro`]}` : v;
        }, sufijo });
        if (p.tipo === 'abierta') {
          cols.push({ key: `${p.id}__tema`, titulo: 'Tema', valor: r => r.analisis?.[p.id]?.tema });
          cols.push({ key: `${p.id}__sent`, titulo: 'Sentimiento', valor: r => r.analisis?.[p.id]?.sentimiento });
        }
      }
    });
    return cols;
  }, [data]);

  // Serie acumulada a partir de fechas sueltas, por día o por mes.
  const serie = (fechas, porDia) => {
    const by = {};
    fechas.forEach(f => {
      const d = soloFecha(f);
      if (!d) return;
      const k = porDia ? d : d.slice(0, 7);
      by[k] = (by[k] || 0) + 1;
    });
    let acc = 0;
    return Object.keys(by).sort().map(k => {
      acc += by[k];
      return { mes: porDia ? k.slice(5).replace('-', '/') : k.slice(2), nuevos: by[k], acumulado: acc };
    });
  };

  const tendencia = useMemo(
    () => serie(filtrados.map(e => e.fecha_registro), granReg === 'dia'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtrados, granReg]
  );

  // Certificados por mes: la fecha es la del último paso terminado del curso, así que
  // un contenido reimportado agrupa en esa fecha lo anterior (se avisa en el panel).
  const tendenciaCert = useMemo(
    () => serie(filtrados.flatMap(e => Object.values(e.certs_fechas || {})), granCert === 'dia'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtrados, granCert]
  );

  const comparacion = useMemo(() => {
    if (!compara.desde || !compara.hasta) return null;
    const base = estudiantes.filter(e => {
      const d = soloFecha(e.fecha_registro);
      return d && d >= compara.desde && d <= compara.hasta;
    });
    const p = metricas(base);
    return [
      ['Registrados', m.n, p.n, false],
      ['En progreso', m.prog, p.prog, false],
      ['Certificados', m.cert, p.cert, false],
      ['Avance ponderado', m.avance, p.avance, true],
      ['Tasa de certificación', m.tasa, p.tasa, true],
    ];
  }, [compara, estudiantes, m]);

  const modAgg = useMemo(() => modMeta.map(mo => {
    const key = String(mo.documento_id);
    const arr = filtrados.map(e => e.mod?.[key]?.pct || 0);
    const compl = filtrados.filter(e => e.mod?.[key]?.completo).length;
    const avg = arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0;
    return { ...mo, key, avg, compl, total: filtrados.length };
  }), [modMeta, filtrados]);

  const evalAgg = useMemo(() => evalMeta.map(mo => {
    const key = String(mo.documento_id);
    const presentes = filtrados.map(e => e.eval?.[key]).filter(Boolean);
    const n = presentes.length;
    const avg = n ? Math.round(presentes.reduce((a, b) => a + (b.pct || 0), 0) / n) : 0;
    const aprob = presentes.filter(x => x.aprobado).length;
    return { ...mo, key, n, avg, aprob, aprobPct: n ? Math.round(aprob / n * 100) : 0 };
  }), [evalMeta, filtrados]);

  const reqAgg = useMemo(() => reqMeta.map(r => {
    const compl = filtrados.filter(e => e.req?.[r.key]).length;
    return { ...r, compl, total: filtrados.length, pct: filtrados.length ? Math.round(compl / filtrados.length * 100) : 0 };
  }).sort((a, b) => a.titulo.localeCompare(b.titulo, undefined, { numeric: true })), [reqMeta, filtrados]);

  // Journey: dónde está cada quien (módulo en curso, sin iniciar o certificado).
  // Journey como el modelo: una fila por ruta de certificación (en proyectos que
  // certifican por perfil, cada certificado es una audiencia con sus módulos). Los
  // módulos que comparten todas las rutas son «comunes»; el resto, especialización.
  const journeyRutas = useMemo(() => {
    const tituloDoc = Object.fromEntries(modMeta.map(mo => [String(mo.documento_id), mo.titulo]));
    const conRutas = data?.certifica_por_perfil && certMeta.length > 0;
    const rutas = conRutas
      ? certMeta.map(c => ({
        key: c.key,
        nombre: (c.etiquetas || []).join(', ') || c.nombre,
        docs: (c.documentos || []).map(String).filter(d => tituloDoc[d]),
        estudiantes: filtrados.filter(e => e.cert_key === c.key),
      })).sort((a, b) => b.estudiantes.length - a.estudiantes.length)
      : [{ key: 'todos', nombre: null, docs: modMeta.map(mo => String(mo.documento_id)), estudiantes: filtrados }];
    const comunes = new Set(rutas.length > 1
      ? rutas[0].docs.filter(d => rutas.every(r => r.docs.includes(d)))
      : rutas[0]?.docs || []);
    const filas = rutas.map(r => {
      const est = r.estudiantes;
      const celdas = [
        { label: 'No han iniciado', n: est.filter(e => e.estado === 'Sin iniciar').length, kind: 'none' },
        ...r.docs.map((d, k) => ({
          label: `M${k + 1}`,
          titulo: tituloDoc[d],
          n: est.filter(e => e.estado === 'En progreso' && e.modulo_actual === tituloDoc[d]).length,
          kind: comunes.has(d) ? 'core' : 'branch',
        })),
      ];
      const alDia = est.filter(e => e.estado === 'En progreso' && e.modulo_actual === 'Contenido al día').length;
      if (alDia) celdas.push({ label: 'Contenido al día', n: alDia, kind: 'core' });
      celdas.push({ label: 'Certificados', n: est.filter(e => e.cumple).length, kind: 'done' });
      return { ...r, celdas };
    });
    const sinRuta = conRutas ? filtrados.filter(e => !e.cert_key).length : 0;
    return { filas, hayEspecializacion: filas.some(f => f.celdas.some(c => c.kind === 'branch')), nComunes: comunes.size, sinRuta };
  }, [filtrados, modMeta, certMeta, data]);

  // ── Consultas al asistente de IA ──
  // Lo que los estudiantes le preguntan al asistente del curso, con el tema que le
  // asignó la IA (se clasifica una vez en el servidor). Se filtra por los estudiantes
  // visibles, igual que el resto del tablero.
  const [temaConsulta, setTemaConsulta] = useState(null);
  const [modoConsulta, setModoConsulta] = useState('');
  const [consultasMax, setConsultasMax] = useState(25);
  const consultas = useMemo(() => {
    const todas = data?.consultas?.preguntas || [];
    if (!todas.length) return null;
    const filtrando = filtrados.length !== (data?.estudiantes || []).length;
    const correos = new Set(filtrados.map(e => (e.email || '').toLowerCase()));
    const nombreDe = new Map(filtrados.map(e => [(e.email || '').toLowerCase(), e.nombre]));
    const lista = (filtrando ? todas.filter(p => correos.has((p.email || '').toLowerCase())) : todas)
      .map(p => ({ ...p, nombre: nombreDe.get((p.email || '').toLowerCase()) || p.email }));
    const contar = (vals) => {
      const m = new Map();
      vals.forEach(v => m.set(v, (m.get(v) || 0) + 1));
      return [...m].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    };
    const personas = new Set(lista.map(p => (p.email || '').toLowerCase())).size;
    const temas = contar(lista.map(p => p.tema || 'Sin clasificar'));
    const utiles = temas.filter(t => t.name !== 'Fuera de tema o sin contenido' && t.name !== 'Sin clasificar');
    const porMes = {};
    lista.forEach(p => { const k = soloFecha(p.fecha).slice(0, 7); if (k) porMes[k] = (porMes[k] || 0) + 1; });
    return {
      lista, personas, temas, utiles,
      total: lista.length,
      totalGeneral: todas.length,
      filtrando,
      documentos: contar(lista.map(p => p.documento || 'Sin contenido')),
      modos: contar(lista.map(p => (p.modo === 'mentor' ? 'Modo mentor' : p.modo === 'evaluacion' ? 'Evaluación' : 'Modo consulta'))),
      meses: Object.keys(porMes).sort().map(k => ({ name: k.slice(2), value: porMes[k] })),
    };
  }, [data, filtrados]);

  // ── Demografía ──
  // Quiénes son (por los campos elegidos en la pestaña: país, ciudad…) y cuándo se
  // conectan. `e.act` trae la actividad por franja «día*24 + hora» en la hora local
  // de cada estudiante, sin las cargas masivas (migraciones).
  const demografia = useMemo(() => {
    if (pestanaActual?.tipo !== 'demografico') return null;
    const campos = (pestanaActual.segmentos || []).filter(c => c !== 'convenio' || data?.tiene_convenios);
    const franjas = new Array(168).fill(0);
    const porSeg = new Map();
    const principal = campos[0] || null;
    let conActividad = 0;
    filtrados.forEach(e => {
      const act = e.act || null;
      const clave = principal ? (valorSegmento(e, principal) || SIN_DATO) : '';
      const k = claveSegmento(clave);
      const fila = porSeg.get(k) || { name: clave, estudiantes: 0, activos: 0, franjas: new Array(168).fill(0) };
      fila.estudiantes++;
      if (act && Object.keys(act).length) {
        conActividad++;
        fila.activos++;
        Object.entries(act).forEach(([f, n]) => { franjas[+f] += n; fila.franjas[+f] += n; });
      }
      porSeg.set(k, fila);
    });
    const porHora = (fr) => Array.from({ length: 24 }, (_, h) => [0, 1, 2, 3, 4, 5, 6].reduce((a, d) => a + fr[d * 24 + h], 0));
    const porDia = (fr) => Array.from({ length: 7 }, (_, d) => fr.slice(d * 24, d * 24 + 24).reduce((a, x) => a + x, 0));
    const pico = (arr) => { const max = Math.max(...arr); return max > 0 ? arr.indexOf(max) : null; };
    const total = franjas.reduce((a, x) => a + x, 0);
    const horas = porHora(franjas);
    const dias = porDia(franjas);
    return {
      campos,
      principal,
      franjas, horas, dias, total, conActividad,
      horaPico: pico(horas), diaPico: pico(dias),
      segmentos: campos.map(c => ({ campo: c, etiqueta: etiquetaSegmento(c, perfilMeta), filas: agrupar(filtrados, c, valorSegmento) })),
      tabla: [...porSeg.values()].map(f => ({
        ...f,
        actividad: f.franjas.reduce((a, x) => a + x, 0),
        horaPico: pico(porHora(f.franjas)),
        diaPico: pico(porDia(f.franjas)),
      })).sort((a, b) => b.estudiantes - a.estudiantes),
    };
  }, [pestanaActual, filtrados, data, perfilMeta]);

  // ── Calidad de datos ──
  // Como el modelo: cuentas conciliadas por número de identificación (sin puntos ni
  // espacios); en cada grupo repetido se sugiere conservar la de mayor avance.
  const calidad = useMemo(() => {
    const meta = data?.calidad_meta || {};
    const asignaciones = meta.asignaciones || [];
    const normDoc = (d) => String(d || '').replace(/[\s.,-]/g, '').toUpperCase();
    const porDoc = new Map();
    filtrados.forEach(e => {
      const d = normDoc(e.dni);
      if (d) porDoc.set(d, [...(porDoc.get(d) || []), e]);
    });
    const grupos = [...porDoc.entries()].filter(([, l]) => l.length > 1);
    const fechaAct = (e) => (e.ultima_actividad ? toDateUTC(e.ultima_actividad)?.getTime() || 0 : 0);
    const duplicadas = [];
    grupos.forEach(([doc, lista], gi) => {
      const orden = [...lista].sort((a, b) => (b.avance || 0) - (a.avance || 0) || fechaAct(b) - fechaAct(a));
      orden.forEach((e, k) => duplicadas.push({ grupo: gi + 1, doc, e, conservar: k === 0 }));
    });

    const inc = [];
    const agregar = (e, tipo, severidad, accion) => inc.push({
      tipo, severidad, accion, nombre: e.nombre, email: e.email, dni: e.dni, pais: e.pais, estado: e.estado, avance: e.avance,
    });
    filtrados.forEach(e => {
      if (!e.dni) agregar(e, 'Sin identificación', 'Media', 'Validar dato de registro');
      else if ((porDoc.get(normDoc(e.dni)) || []).length > 1) agregar(e, 'Identificación duplicada', 'Alta', 'Conciliar con la cuenta de mayor avance');
      if (data?.certifica_por_perfil && !e.cert_key) agregar(e, 'Sin ruta de certificación', 'Alta', 'Revisar el perfil del registro');
      asignaciones.forEach(a => {
        const v = valorSegmento(e, a.campo);
        if (!v) agregar(e, `Sin ${a.etiqueta.toLowerCase()}`, 'Operativa', `Asignar ${a.etiqueta.toLowerCase()}`);
        else if (a.por_defecto && claveSegmento(v) === claveSegmento(a.por_defecto)) {
          agregar(e, `${a.etiqueta}: ${a.por_defecto}`, 'Operativa', `Asignar ${a.etiqueta.toLowerCase()} o confirmar ${a.por_defecto}`);
        }
      });
      if (!e.pais) agregar(e, 'Sin país', 'Baja', 'Completar el registro');
      if (!e.telefono) agregar(e, 'Sin teléfono', 'Baja', 'Completar el registro');
      if (!e.ultima_actividad) agregar(e, 'Nunca ingresó', 'Seguimiento', 'Invitar a iniciar');
      else if ((e.dias_inactividad ?? 0) >= 60 && !e.cumple) agregar(e, 'Inactivo +60 días', 'Seguimiento', 'Contactar para retomar');
    });
    const PESO = { Alta: 0, Media: 1, Operativa: 2, Baja: 3, Seguimiento: 4 };
    inc.sort((a, b) => PESO[a.severidad] - PESO[b.severidad]);   // estable: dentro, por nombre
    const kpiAsign = asignaciones.map(a => ({
      ...a,
      n: filtrados.filter(e => {
        const v = valorSegmento(e, a.campo);
        return !v || (a.por_defecto && claveSegmento(v) === claveSegmento(a.por_defecto));
      }).length,
    }));
    return {
      incidencias: inc,
      tipos: contar(inc, 'tipo'),
      conProblema: new Set(inc.map(i => i.email)).size,
      grupos: grupos.length,
      duplicadas,
      descartables: duplicadas.filter(d => !d.conservar).length,
      unicos: filtrados.length - duplicadas.filter(d => !d.conservar).length,
      sinRuta: data?.certifica_por_perfil ? filtrados.filter(e => !e.cert_key).length : null,
      kpiAsign,
      cuentasFuente: meta.cuentas_fuente ?? (data?.estudiantes || []).length,
      excluidas: meta.excluidas || 0,
      // Cuentas creadas por el importador de la plataforma externa: no son
      // registros de la web, y por eso se dicen aparte en «Registrados».
      importadas: meta.importadas || 0,
    };
  }, [filtrados, data]);

  // Comunicaciones: se recalcula con los filtros, a partir de los correos de cada
  // estudiante (key del mensaje, estado, abrió, clic, baja).
  const [listaCorreo, setListaCorreo] = useState('todos');
  // Filtro por tipo de correo y, dentro de Comunicados, por campaña (un comunicado).
  const [tipoCorreo, setTipoCorreo] = useState('');
  const [campana, setCampana] = useState('');
  const todosMensajes = data?.comunicaciones?.mensajes || [];
  const campanas = todosMensajes.filter(m => m.tipo === 'comunicado');
  const correos = useMemo(() => {
    const mensajes = todosMensajes
      .filter(m => !tipoCorreo || m.tipo === tipoCorreo)
      .filter(m => !(tipoCorreo === 'comunicado' && campana) || m.key === campana)
      .map(m => ({
        ...m, enviados: 0, entregados: 0, abrieron: 0, clic: 0, rebotes: 0, quejas: 0, bajas: 0, fallidos: 0,
      }));
    const porKey = Object.fromEntries(mensajes.map(m => [m.key, m]));
    const tot = { personas: 0, enviados: 0, entregados: 0, abrieron: 0, clic: 0, rebotes: 0, quejas: 0, bajas: 0 };
    const listas = { todos: [], abrieron: [], no_abrieron: [], rebotes: [], quejas: [], bajas: [], clic: [] };

    filtrados.forEach(e => {
      // Solo los correos de los mensajes que pasan el filtro de tipo / campaña.
      const cs = (e.correos || []).filter(([key]) => porKey[key]);
      if (!cs.length) return;
      tot.personas++;
      let abrio = false; let entregado = false;
      const marca = { rebotes: false, quejas: false, bajas: false, clic: false };
      cs.forEach(([key, estado, a, k, b]) => {
        const m = porKey[key];
        tot.enviados++;
        if (m) m.enviados++;
        if (estado === 'entregado') { tot.entregados++; entregado = true; if (m) m.entregados++; }
        if (a) { tot.abrieron++; abrio = true; if (m) m.abrieron++; }
        if (k) { tot.clic++; marca.clic = true; if (m) m.clic++; }
        if (b) { tot.bajas++; marca.bajas = true; if (m) m.bajas++; }
        if (estado === 'rebote') { tot.rebotes++; marca.rebotes = true; if (m) m.rebotes++; }
        if (estado === 'queja') { tot.quejas++; marca.quejas = true; if (m) m.quejas++; }
        if (estado === 'fallido' && m) m.fallidos++;
      });
      const fila = {
        nombre: e.nombre, email: e.email, pais: e.pais, estado: e.estado,
        detalle: cs.map(([key, estado, a, k, b]) => ({
          titulo: porKey[key]?.titulo || '—', estado, a, k, b,
        })),
      };
      listas.todos.push(fila);
      if (abrio) listas.abrieron.push(fila);
      if (entregado && !abrio) listas.no_abrieron.push(fila);
      Object.keys(marca).forEach(k => { if (marca[k]) listas[k].push(fila); });
    });
    return { mensajes, tot, listas, hay: mensajes.length > 0, hayAlguno: todosMensajes.length > 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtrados, tipoCorreo, campana]);

  // ── Orden y paginación de la tabla de estudiantes ──
  const ordenados = useMemo(() => {
    const { campo, dir } = orden;
    const val = (e) => {
      if (campo === 'perfil0') return perfilMeta[0] ? e.perfil?.[perfilMeta[0].key] : e.nombre;
      return e[campo];
    };
    return [...visibles].sort((a, b) => {
      let x = val(a), y = val(b);
      x = x ?? (typeof y === 'number' ? -1 : '');
      y = y ?? (typeof x === 'number' ? -1 : '');
      return (typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x).localeCompare(String(y), 'es')) * dir;
    });
  }, [visibles, orden, perfilMeta]);

  // Catálogo de certificados (un certificado por curso): una persona puede ganar
  // varios, así que el total de certificados no es el número de certificados del
  // proyecto ni el de personas. Se recalcula sobre los filtros activos; «descargado»
  // es el PDF que el estudiante ya bajó (los demás los tiene disponibles).
  const catalogo = useMemo(() => {
    const meta = data?.catalogo_meta || [];
    if (!meta.length) return null;
    const porCurso = meta.map(c => ({ ...c, cumplen: 0, descargados: 0 }));
    const idx = {};
    porCurso.forEach((c, i) => { idx[c.key] = i; });
    let ganados = 0, descargados = 0, personas = 0, personasConDescarga = 0, historial = 0;
    filtrados.forEach(e => {
      const keys = e.certs_keys || [];
      const baj = e.certs_emitidos || [];
      // El «+1» del historial es para quien llegó certificado de otra plataforma y no
      // tiene avance aquí. Si además cumplió requisitos, sus certificados ya están en
      // `ganados`: sumarle el del historial lo contaría dos veces.
      if (e.cert_historial && !keys.length) historial++;
      if (keys.length || e.cert_historial) personas++;
      if (baj.length) personasConDescarga++;
      ganados += keys.length;
      descargados += baj.length;
      keys.forEach(k => { if (idx[k] != null) porCurso[idx[k]].cumplen++; });
      baj.forEach(k => { if (idx[k] != null) porCurso[idx[k]].descargados++; });
    });
    return {
      porCurso: porCurso.sort((a, b) => b.cumplen - a.cumplen),
      ganados, descargados, personas, personasConDescarga, historial,
      promedio: personas ? (ganados + historial) / personas : 0,
      nombres: idx && Object.fromEntries(meta.map(c => [c.key, c.nombre])),
    };
  }, [data, filtrados]);

  // Compromiso de metas: inscritos reales de cada grupo contra lo prometido.
  const metas = useMemo(() => {
    const cfg = normalizarMetas(data?.metas);
    const filas = cfg.filas.filter(f => f.valores.length || f.target);
    if (!filas.length) return null;
    const deFila = {};
    filas.forEach(f => f.valores.forEach(v => { deFila[v] = f.id; }));
    const acc = {};
    filas.forEach(f => { acc[f.id] = { ...f, inscritos: 0, certificados: 0 }; });
    let fuera = 0, fueraCert = 0;
    filtrados.forEach(e => {
      const v = valorSegmento(e, cfg.campo) || SIN_DATO;
      // Con regiones, la meta puede estar escrita con la región o con sus países.
      const real = cfg.campo === 'pais' ? e.pais_real : null;
      const id = deFila[v] || (real ? deFila[real] : null);
      if (id && acc[id]) { acc[id].inscritos++; if (e.cumple) acc[id].certificados++; }
      else { fuera++; if (e.cumple) fueraCert++; }
    });
    const rows = filas.map(f => {
      const r = acc[f.id];
      return { ...r, pct: r.target ? r.inscritos / r.target : null, falta: Math.max(0, r.target - r.inscritos) };
    });
    const target = rows.reduce((a, r) => a + r.target, 0);
    const inscritos = rows.reduce((a, r) => a + r.inscritos, 0);
    return {
      ...cfg, rows, target, inscritos, fuera, fueraCert,
      certificados: rows.reduce((a, r) => a + r.certificados, 0),
      pct: target ? inscritos / target : null,
      cumplidas: rows.filter(r => r.target && r.inscritos >= r.target).length,
    };
  }, [data, filtrados]);

  // Certificados que vienen del historial (perfiles marcados como ya certificados).
  const historialN = useMemo(() => filtrados.filter(e => e.cert_historial).length, [filtrados]);

  // Educare: el cruce con la plataforma externa. Viene en null cuando el proyecto
  // no importa esos datos, y entonces no se muestra nada de esto.
  const educare = data?.educare_meta || null;
  // learning.ateneo.co: la plataforma partner. Mismo trato que Educare.
  const learning = data?.learning_meta || null;

  const PER_PAGE = 50;
  const VER_POR_CERT = 50;
  const totalPages = Math.max(1, Math.ceil(ordenados.length / PER_PAGE));
  const pageSafe = Math.min(page, totalPages - 1);
  const paged = useMemo(() => ordenados.slice(pageSafe * PER_PAGE, pageSafe * PER_PAGE + PER_PAGE), [ordenados, pageSafe]);
  const ordenarPor = (campo) => setOrden(o => ({ campo, dir: o.campo === campo ? -o.dir : 1 }));

  // ── Exportaciones ──
  const nombreArchivo = (sufijo) => `${(data?.nombre || 'dashboard')}_${sufijo}`;

  const exportarPerfiles = () => {
    const cols = (perfilMeta.length ? perfilMeta.map(c => c.etiqueta) : ['Nombre', 'Correo', 'País', 'Especialidad'])
      .concat(['Estado', 'Avance %', 'Certificación', 'Registro', 'Última actividad', 'Días inactivo']);
    const rows = ordenados.map(e => (perfilMeta.length
      ? perfilMeta.map(c => e.perfil?.[c.key] ?? '')
      : [e.nombre, e.email, e.pais || '', e.especialidad || ''])
      .concat([e.estado, e.avance ?? 0, e.cumple ? 'Cumple' : 'Pendiente',
        fmtFecha(e.fecha_registro), e.ultima_actividad ? fmtFecha(e.ultima_actividad) : '',
        e.dias_inactividad ?? '']));
    bajarCSV(nombreArchivo('estudiantes'), cols, rows);
  };

  const exportarCompleto = () => {
    const cols = perfilMeta.length ? perfilMeta.map(c => c.etiqueta) : ['Nombre', 'Correo', 'País', 'Especialidad'];
    if (data.tiene_convenios && !perfilMeta.some(c => c.key === 'convenio')) cols.push('Convenio');
    cols.push('Estado', 'Avance %');
    modMeta.forEach(mo => cols.push(`${mo.titulo} %`));
    evalMeta.forEach(mo => { cols.push(`Eval ${mo.titulo} %`); cols.push(`Eval ${mo.titulo} aprobado`); });
    reqMeta.forEach(r => cols.push(`Req: ${r.titulo}`));
    if (data.regiones) cols.push('País (detalle)');
    if (catalogo) cols.push('Certificados ganados', 'PDF descargados', 'Certificados');
    // Perfiles que llegaron certificados de otra plataforma: sin esta columna, en la
    // descarga parecen «Certificado» con cero certificados ganados.
    if ((data.subroles_certificados || []).length) cols.push('Certificado previo (antes de la plataforma)');
    cols.push('Certificación', 'Registro', 'Última actividad');
    const rows = ordenados.map(e => {
      const r = perfilMeta.length
        ? perfilMeta.map(c => e.perfil?.[c.key] ?? '')
        : [e.nombre, e.email, e.pais || '', e.especialidad || ''];
      if (data.tiene_convenios && !perfilMeta.some(c => c.key === 'convenio')) r.push(e.convenio || '');
      r.push(e.estado, e.avance ?? 0);
      modMeta.forEach(mo => r.push(e.mod?.[String(mo.documento_id)]?.pct ?? 0));
      evalMeta.forEach(mo => {
        const ev = e.eval?.[String(mo.documento_id)];
        r.push(ev ? ev.pct : '—'); r.push(ev ? (ev.aprobado ? 'Sí' : 'No') : '—');
      });
      reqMeta.forEach(rq => r.push(e.req?.[rq.key] ? 'Sí' : 'No'));
      if (data.regiones) r.push(e.pais_real || '');
      if (catalogo) r.push((e.certs_keys || []).length, (e.certs_emitidos || []).length,
        (e.certs_keys || []).map(k => catalogo.nombres[k] || k).join(' | '));
      if ((data.subroles_certificados || []).length) r.push(e.cert_historial ? 'Sí' : 'No');
      r.push(e.cumple ? 'Cumple' : 'Pendiente', fmtFecha(e.fecha_registro),
        e.ultima_actividad ? fmtFecha(e.ultima_actividad) : '');
      return r;
    });
    bajarCSV(nombreArchivo('completo'), cols, rows);
  };

  const exportarMetas = () => {
    if (!metas) return;
    bajarCSV(nombreArchivo('metas'),
      [etiquetaSegmento(metas.campo, perfilMeta), 'Incluye', 'Meta', 'Inscritos', '% de la meta', 'Faltan', 'Certificados'],
      metas.rows.map(r => [r.etiqueta || r.valores.join(' | '), r.valores.join(' | '), r.target, r.inscritos,
        r.pct == null ? '' : Math.round(r.pct * 100), r.falta, r.certificados])
        .concat([['Total', '', metas.target, metas.inscritos,
          metas.pct == null ? '' : Math.round(metas.pct * 100),
          Math.max(0, metas.target - metas.inscritos), metas.certificados]]));
  };

  const exportarCatalogo = () => {
    if (!catalogo) return;
    bajarCSV(nombreArchivo('certificados_por_curso'),
      ['Certificado', 'Certificados ganados', 'PDF descargados', '% de los registrados'],
      catalogo.porCurso.map(c => [c.nombre, c.cumplen, c.descargados,
        m.n ? Math.round(c.cumplen / m.n * 100) : 0]));
  };

  const exportarCatalogoEstudiantes = () => {
    if (!catalogo) return;
    bajarCSV(nombreArchivo('certificados_por_estudiante'),
      ['Estudiante', 'Correo', 'Certificados ganados', 'PDF descargados', 'Cuáles'],
      ordenados.filter(e => (e.certs_keys || []).length).map(e => [
        e.nombre, e.email, (e.certs_keys || []).length, (e.certs_emitidos || []).length,
        (e.certs_keys || []).map(k => catalogo.nombres[k] || k).join(' | '),
      ]));
  };

  const exportarCertificado = (c, suyos, mods, evs) => {
    const cols = ['Estudiante', 'Correo']
      .concat(mods.map(x => `${x.titulo} %`))
      .concat(evs.flatMap(x => [`Eval ${x.titulo} %`, `Eval ${x.titulo} aprobado`]))
      .concat(['Certificación']);
    const rows = suyos.map(e => [e.nombre, e.email]
      .concat(mods.map(x => e.mod?.[String(x.documento_id)]?.pct ?? 0))
      .concat(evs.flatMap(x => {
        const ev = e.eval?.[String(x.documento_id)];
        return [ev ? ev.pct : '—', ev ? (ev.aprobado ? 'Sí' : 'No') : '—'];
      }))
      .concat([e.cumple ? 'Cumple' : 'Pendiente']));
    bajarCSV(nombreArchivo(c.nombre), cols, rows);
  };

  // Demografía: un estudiante por fila (sus campos, su actividad, su hora y día de más
  // actividad) y un resumen por segmento con la actividad de cada hora.
  const DIAS_SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const exportarDemografiaEstudiantes = () => {
    const dm = demografia;
    if (!dm) return;
    const pico = (arr) => { const max = Math.max(...arr); return max > 0 ? arr.indexOf(max) : null; };
    bajarCSV(nombreArchivo('demografia_estudiantes'),
      ['Nombre', 'Correo', ...dm.segmentos.map(sg => sg.etiqueta), 'Actividades', 'Hora de más actividad', 'Día de más actividad', 'Última actividad'],
      filtrados.map(e => {
        const fr = new Array(168).fill(0);
        Object.entries(e.act || {}).forEach(([f, n]) => { fr[+f] += n; });
        const horas = Array.from({ length: 24 }, (_, h) => [0, 1, 2, 3, 4, 5, 6].reduce((a, d) => a + fr[d * 24 + h], 0));
        const dias = Array.from({ length: 7 }, (_, d) => fr.slice(d * 24, d * 24 + 24).reduce((a, x) => a + x, 0));
        const total = fr.reduce((a, x) => a + x, 0);
        const h = pico(horas);
        const d = pico(dias);
        return [e.nombre, e.email, ...dm.segmentos.map(sg => valorSegmento(e, sg.campo) || ''),
          total, h === null ? '' : `${h}:00-${h + 1}:00`, d === null ? '' : DIAS_SEMANA[d], e.ultima_actividad ? fmtFecha(e.ultima_actividad) : ''];
      }));
  };
  const exportarDemografiaResumen = () => {
    const dm = demografia;
    if (!dm) return;
    const etiqueta = dm.segmentos[0]?.etiqueta || 'Segmento';
    const porHora = (fr) => Array.from({ length: 24 }, (_, h) => [0, 1, 2, 3, 4, 5, 6].reduce((a, d) => a + fr[d * 24 + h], 0));
    bajarCSV(nombreArchivo('demografia_resumen'),
      [etiqueta, 'Estudiantes', 'Con actividad', 'Actividades', 'Hora de más actividad', 'Día de más actividad',
        ...DIAS_SEMANA.map(d => `Actividades ${d}`), ...Array.from({ length: 24 }, (_, h) => `${h}:00`)],
      [
        ...dm.tabla.map(f => [f.name, f.estudiantes, f.activos, f.actividad,
          f.horaPico === null ? '' : `${f.horaPico}:00-${f.horaPico + 1}:00`, f.diaPico === null ? '' : DIAS_SEMANA[f.diaPico],
          ...DIAS_SEMANA.map((_, d) => f.franjas.slice(d * 24, d * 24 + 24).reduce((a, x) => a + x, 0)),
          ...porHora(f.franjas)]),
        ['Total', filtrados.length, dm.conActividad, dm.total,
          dm.horaPico === null ? '' : `${dm.horaPico}:00-${dm.horaPico + 1}:00`, dm.diaPico === null ? '' : DIAS_SEMANA[dm.diaPico],
          ...dm.dias, ...dm.horas],
      ]);
  };

  const exportarConsultas = () => {
    if (!consultas) return;
    bajarCSV(nombreArchivo('consultas_asistente'),
      ['Fecha', 'Estudiante', 'Correo', 'Contenido', 'Modo', 'Tema', 'Pregunta'],
      consultas.lista.map(p => [fmtFecha(p.fecha), p.nombre, p.email, p.documento || '', p.modo, p.tema || '', p.texto]));
  };

  const exportarIncidencias = () => bajarCSV(
    nombreArchivo('calidad_de_datos'),
    ['Tipo', 'Severidad', 'Acción sugerida', 'Estudiante', 'Correo', 'Documento', 'País', 'Estado', 'Avance %'],
    calidad.incidencias.map(i => [i.tipo, i.severidad, i.accion, i.nombre, i.email, i.dni || '', i.pais || '', i.estado, i.avance ?? 0])
  );

  const exportarCorreos = () => {
    const filas = correos.listas[listaCorreo] || [];
    bajarCSV(
      nombreArchivo(`comunicaciones_${tipoCorreo || 'todos'}${campana && tipoCorreo === 'comunicado' ? '_campana' : ''}_${listaCorreo}`),
      ['Estudiante', 'Correo', 'País', 'Estado en el programa', 'Mensajes'],
      filas.map(f => [f.nombre, f.email, f.pais || '', f.estado,
        f.detalle.map(d => `${d.titulo}: ${ETQ_CORREO[d.estado] || d.estado}${d.a ? ', abrió' : ''}${d.k ? ', clic' : ''}${d.b ? ', baja' : ''}`).join(' | ')])
    );
  };

  const exportarEncuesta = () => {
    const resp = encuesta?.resp || [];
    bajarCSV(nombreArchivo('encuesta'),
      ['Nombre', 'Correo', 'País', ...columnasEncuesta.map(c => c.titulo), 'Fecha'],
      resp.map(r => [r.nombre, r.email, r.pais || '', ...columnasEncuesta.map(c => c.valor(r) ?? ''), fmtFecha(r.fecha)]));
  };

  // ── Pantallas de acceso ──
  if (notFound) {
    return (
      <ThemeProvider theme={tema}>
        <GlobalDash />
        <Center><Gate><h2>Dashboard no encontrado</h2><Note>El enlace no es válido o fue desactivado.</Note></Gate></Center>
      </ThemeProvider>
    );
  }

  if (!accessToken || !data) {
    const logo = resolveLogo(info?.proyecto?.logo);
    return (
      <ThemeProvider theme={tema}>
        <GlobalDash />
        <Center>
          <Gate>
            {logo && <img src={logo} alt="" style={{ height: 46, objectFit: 'contain', marginBottom: 10 }} />}
            <h2>{info?.nombre || 'Dashboard'}</h2>
            <Note style={{ margin: '0 auto' }}>{info?.proyecto?.nombre}</Note>
            {!pasoCodigo ? (
              <form onSubmit={submitEmail}>
                <GateInput type="email" placeholder="Ingresa tu correo autorizado" value={email}
                           onChange={e => setEmail(e.target.value)} autoFocus />
                <Primary type="submit" disabled={loading}>{loading ? 'Enviando…' : 'Enviarme un código'}</Primary>
              </form>
            ) : (
              <form onSubmit={submitCodigo}>
                {aviso && <Note style={{ margin: '12px auto 0' }}>{aviso}</Note>}
                <GateInput inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="Código de 6 dígitos"
                           value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus
                           style={{ letterSpacing: 6, textAlign: 'center', fontSize: 20 }} />
                <Primary type="submit" disabled={loading}>{loading ? 'Verificando…' : 'Ingresar'}</Primary>
                <div style={{ marginTop: 10 }}>
                  <Ghost type="button" onClick={() => { setPasoCodigo(false); setCodigo(''); setError(''); setAviso(''); }}>
                    Usar otro correo o pedir un código nuevo
                  </Ghost>
                </div>
              </form>
            )}
            {error && <Err>{error}</Err>}
            <div style={{ marginTop: 16 }}>
              <Ghost type="button" onClick={alternarTema}>{modo === 'dark' ? '☀ Modo claro' : '🌙 Modo oscuro'}</Ghost>
            </div>
          </Gate>
        </Center>
      </ThemeProvider>
    );
  }

  const logo = resolveLogo(info?.proyecto?.logo);
  const hayFiltros = filtrosActivos > 0 || segFilter || focus;
  const pieTitulo = focus ? focus.titulo : 'Avance general';

  const limpiar = () => { setF(FILTROS_VACIOS); setSegFilter(null); setFocus(null); };

  return (
    <ThemeProvider theme={tema}>
      <GlobalDash />
      <Wrap>
        <Top>
          <Brand>
            {logo && <img src={logo} alt="" style={{ height: 40, objectFit: 'contain' }} />}
            <div style={{ minWidth: 0 }}>
              <BrandName>{data.nombre}</BrandName>
              <BrandSub>{data.certificado_nombre} · Alcance: {data.filtro_label || data.scope?.label}</BrandSub>
            </div>
          </Brand>
          <TopTools>
            <CutBox><small>Información hasta</small>{fmtFecha(data.generado_en || new Date().toISOString())}</CutBox>
            <ThemeBtn type="button" onClick={alternarTema} title="Cambiar entre modo claro y oscuro">
              {modo === 'dark' ? '☀ Claro' : '🌙 Oscuro'}
            </ThemeBtn>
            <ThemeBtn type="button" onClick={() => loadData(accessToken)} disabled={loading}>
              {loading ? '…' : '↻ Actualizar'}
            </ThemeBtn>
          </TopTools>
        </Top>

        {/* Filtros: mandan sobre todas las pestañas */}
        <FilterBar>
          <FilterSummary>
            <span>Vista analítica</span>
            <strong>{nf.format(filtrados.length)} estudiantes</strong>
            <span>{filtrosActivos ? `${filtrosActivos} filtros activos` : 'Sin filtros'}</span>
          </FilterSummary>
          <Field><span>Buscar</span>
            <Input placeholder="Nombre, correo, documento…" value={f.q} onChange={e => setFiltro('q', e.target.value)} />
          </Field>
          <Field><span>Registro desde</span>
            <Input type="date" value={f.regDesde} onChange={e => setFiltro('regDesde', e.target.value)} />
          </Field>
          <Field><span>Registro hasta</span>
            <Input type="date" value={f.regHasta} onChange={e => setFiltro('regHasta', e.target.value)} />
          </Field>
          {hayFechasCert && (
            <>
              <Field><span>Se certificó desde</span>
                <Input type="date" value={f.certDesde} onChange={e => setFiltro('certDesde', e.target.value)} />
              </Field>
              <Field><span>Se certificó hasta</span>
                <Input type="date" value={f.certHasta} onChange={e => setFiltro('certHasta', e.target.value)} />
              </Field>
            </>
          )}
          {opciones.pais.length > 1 && (
            <Field><span>País</span>
              <Select value={f.pais} onChange={e => setFiltro('pais', e.target.value)}>
                <option value="">Todos</option>
                {opciones.pais.map(v => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Field>
          )}
          {opciones.especialidad.length > 1 && (
            <Field><span>Especialidad</span>
              <Select value={f.especialidad} onChange={e => setFiltro('especialidad', e.target.value)}>
                <option value="">Todas</option>
                {opciones.especialidad.map(v => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Field>
          )}
          {opciones.subrol.length > 1 && (
            <Field><span>Perfil</span>
              <Select value={f.subrol} onChange={e => setFiltro('subrol', e.target.value)}>
                <option value="">Todos</option>
                {opciones.subrol.map(v => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Field>
          )}
          {(data.filtros_barra || []).map(campo => {
            const vals = opciones.perfil?.[campo] || [];
            if (vals.length < 2) return null;
            return (
              <Field key={campo}><span>{etiquetaSegmento(campo, perfilMeta)}</span>
                <Select value={f.perfil?.[campo] || ''}
                        onChange={e => setF(prev => ({ ...prev, perfil: { ...(prev.perfil || {}), [campo]: e.target.value } }))}>
                  <option value="">Todos</option>
                  {vals.map(v => <option key={v} value={v}>{v}</option>)}
                </Select>
              </Field>
            );
          })}
          {opciones.convenio.length > 1 && (
            <Field><span>Convenio</span>
              <Select value={f.convenio} onChange={e => setFiltro('convenio', e.target.value)}>
                <option value="">Todos</option>
                {opciones.convenio.map(v => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Field>
          )}
          <Field><span>Estado</span>
            <Select value={f.estado} onChange={e => setFiltro('estado', e.target.value)}>
              <option value="">Todos</option>
              <option value="Sin iniciar">Sin iniciar</option>
              <option value="En progreso">En progreso</option>
              <option value="Certificado">Certificado</option>
            </Select>
          </Field>
          <Field><span>Actividad</span>
            <Select value={f.actividad} onChange={e => setFiltro('actividad', e.target.value)}>
              <option value="all">Todo el tiempo</option>
              <option value="7">Últimos 7 días</option>
              <option value="30">Últimos 30 días</option>
              <option value="90">Últimos 90 días</option>
            </Select>
          </Field>
          <Ghost onClick={limpiar} disabled={!hayFiltros}>Limpiar</Ghost>
        </FilterBar>

        <Tabs>
          {pestanas.map(p => (
            <Tab key={p.id} $active={pestana === p.id} onClick={() => setPestana(p.id)}>{p.titulo}</Tab>
          ))}
        </Tabs>

        <Container>
          {data.capped && <Err style={{ marginBottom: 14 }}>Mostrando los primeros 5000 estudiantes.</Err>}
          {segFilter && (
            <Chips style={{ marginBottom: 12 }}>
              <Chip $active onClick={() => setSegFilter(null)}>{pieTitulo}: {segFilter} ✕</Chip>
            </Chips>
          )}

          {/* ══ RESUMEN ══ */}
          {tipoActual === 'resumen' && (
            <>
              <Heading>
                <div><Eyebrow>Resumen ejecutivo</Eyebrow><H2>Estado del programa</H2></div>
                <Actions><Btn onClick={exportarCompleto}>⬇ Descargar datos</Btn></Actions>
              </Heading>

              <KpiGrid>
                <KpiCard $tone={tema.serieA}>
                  <span>Registrados</span><strong>{nf.format(m.n)}</strong>
                  <small>
                    {calidad.importadas > 0
                      ? <>{nf.format(m.n - calidad.importadas)} se registraron aquí · {nf.format(calidad.importadas)} traídos de la plataforma externa</>
                      : 'Estudiantes en el alcance'}
                  </small>
                </KpiCard>
                <KpiCard $tone={tema.serieC}>
                  <span>Avance ponderado</span><strong>{pct(m.avance, 1)}</strong><small>Sobre los pasos de cada ruta</small>
                </KpiCard>
                <KpiCard $tone={tema.warn}>
                  <span>En progreso</span><strong>{nf.format(m.prog)}</strong><small>Con avance y sin certificar</small>
                </KpiCard>
                {catalogo ? (
                  <>
                    <KpiCard $tone={tema.ok}>
                      <span>Certificados obtenidos</span><strong>{nf.format(catalogo.ganados + catalogo.historial)}</strong>
                      <small>{nf.format(catalogo.descargados)} descargados
                        {catalogo.historial ? ` · ${nf.format(catalogo.historial)} de antes de la plataforma` : ''}
                        {' · '}{catalogo.promedio.toFixed(1)} por persona</small>
                    </KpiCard>
                    <KpiCard $tone={tema.serieB}>
                      <span>Personas certificadas</span><strong>{nf.format(catalogo.personas)}</strong>
                      <small>{pct(m.n ? catalogo.personas / m.n : 0, 1)} de los registrados · con al menos uno</small>
                    </KpiCard>
                  </>
                ) : (
                  <KpiCard $tone={tema.ok}>
                    <span>Certificados</span><strong>{nf.format(m.cert)}</strong>
                    <small>{pct(m.tasa, 1)} de tasa{historialN ? ` · ${nf.format(historialN)} de antes de la plataforma` : ''}</small>
                  </KpiCard>
                )}
                {/* Programa híbrido: los cursos que el alumno hace en Educare o en
                    learning.ateneo.co también certifican, y esos certificados no
                    salen de esta plataforma. Se cuentan aparte, nunca sumados. */}
                {educare && educare.certificados > 0 && (
                  <KpiCard $tone={tema.serieB}>
                    <span>Certificados en Educare</span><strong>{nf.format(educare.certificados)}</strong>
                    <small>{nf.format(educare.con_certificado)} personas · del archivo importado</small>
                  </KpiCard>
                )}
                {learning && learning.certificados > 0 && (
                  <KpiCard $tone={tema.serieC}>
                    <span>Certificados en Learning</span><strong>{nf.format(learning.certificados)}</strong>
                    <small>{nf.format(learning.con_certificado)} personas · learning.ateneo.co</small>
                  </KpiCard>
                )}
                <KpiCard $tone={tema.bad}>
                  <span>Sin iniciar</span><strong>{nf.format(m.sin)}</strong><small>{pct(m.n ? m.sin / m.n : 0)} del total</small>
                </KpiCard>
                {nps && (
                  <KpiCard $tone={tema.lime}>
                    <span>NPS</span>
                    <strong>{nps.nps == null ? '—' : nps.nps}</strong>
                    <small>{nf.format(nps.n || 0)} encuestas · del programa</small>
                  </KpiCard>
                )}
              </KpiGrid>

              <Grid2>
                <Panel>
                  <PanelHead>
                    <div>
                      <h3>Tendencia de registros</h3>
                      <p>Las barras muestran el acumulado y la línea los nuevos registros de cada {granReg === 'dia' ? 'día' : 'mes'}.</p>
                    </div>
                    <Actions>
                      <Ghost onClick={() => setGranReg('dia')} aria-pressed={granReg === 'dia'}
                             style={granReg === 'dia' ? { color: tema.text, borderColor: tema.accent } : undefined}>Por día</Ghost>
                      <Ghost onClick={() => setGranReg('mes')} aria-pressed={granReg === 'mes'}
                             style={granReg === 'mes' ? { color: tema.text, borderColor: tema.accent } : undefined}>Por mes</Ghost>
                    </Actions>
                  </PanelHead>
                  <PanelBody>
                    <Tendencia rows={tendencia}
                               nuevos={granReg === 'dia' ? 'Registros del día' : 'Nuevos registros'} />
                  </PanelBody>
                </Panel>
                <Panel>
                  <PanelHead>
                    <h3>Composición del programa</h3>
                    <p>Volumen y certificación por {opciones.subrol.length > 1 ? 'perfil' : 'país'}.</p>
                  </PanelHead>
                  <PanelBody>
                    <BarrasResultado tema={tema} limite={8}
                      rows={agrupar(filtrados, opciones.subrol.length > 1 ? 'subrol' : 'pais')} />
                  </PanelBody>
                </Panel>
              </Grid2>

              {/* Con catálogo van lado a lado; sin él, la tendencia ocupa todo el ancho. */}
              {(catalogo || tendenciaCert.length > 0) && (
                <Grid2 as={catalogo ? undefined : 'div'}
                       style={catalogo ? undefined : { display: 'block' }}>
              {tendenciaCert.length > 0 && (
                <Panel>
                  <PanelHead>
                    <div>
                      <h3>Tendencia de certificados</h3>
                      <p>Cuántos certificados se obtuvieron cada {granCert === 'dia' ? 'día' : 'mes'} y cómo se acumulan.
                        Un certificado cuenta el día en que la persona terminó el último contenido que le exige; si el
                        proyecto reimportó su contenido, lo anterior aparece agrupado en esa fecha.</p>
                    </div>
                    <Actions>
                      <Ghost onClick={() => setGranCert('dia')} aria-pressed={granCert === 'dia'}
                             style={granCert === 'dia' ? { color: tema.text, borderColor: tema.accent } : undefined}>Por día</Ghost>
                      <Ghost onClick={() => setGranCert('mes')} aria-pressed={granCert === 'mes'}
                             style={granCert === 'mes' ? { color: tema.text, borderColor: tema.accent } : undefined}>Por mes</Ghost>
                    </Actions>
                  </PanelHead>
                  <PanelBody>
                    <Tendencia rows={tendenciaCert}
                               acumulado="Certificados acumulados"
                               nuevos={granCert === 'dia' ? 'Certificados del día' : 'Certificados del mes'}
                               vacio="Todavía no hay certificados con fecha." />
                  </PanelBody>
                </Panel>
              )}

              {catalogo && (
                <Panel>
                  <PanelHead>
                    <div>
                      <h3>Certificados por curso</h3>
                      <p>Cada curso entrega su propio certificado: aquí cuántas personas lo obtuvieron y cuántas
                        ya descargaron el PDF (el resto lo tiene disponible en la plataforma).</p>
                    </div>
                    <Actions>
                      <Btn onClick={exportarCatalogo}>⬇ Por curso</Btn>
                      <Btn onClick={exportarCatalogoEstudiantes}>⬇ Por estudiante</Btn>
                    </Actions>
                  </PanelHead>
                  <PanelBody>
                    <TableWrap>
                      <Table>
                        <thead><tr>
                          <Th>Certificado</Th><Th>Obtenidos</Th><Th>Descargados</Th><Th>% de los registrados</Th>
                        </tr></thead>
                        <tbody>
                          {catalogo.porCurso.map(c => (
                            <tr key={c.key}>
                              <Td>{c.nombre}</Td>
                              <Td style={{ fontWeight: 700 }}>{nf.format(c.cumplen)}</Td>
                              <Td>{nf.format(c.descargados)}</Td>
                              <Td>{pct(m.n ? c.cumplen / m.n : 0)}</Td>
                            </tr>
                          ))}
                          {catalogo.historial > 0 && (
                            <tr>
                              <Td>Certificado previo ({(data.subroles_certificados || []).join(', ') || 'perfil marcado'})</Td>
                              <Td style={{ fontWeight: 700 }}>{nf.format(catalogo.historial)}</Td>
                              <Td>—</Td>
                              <Td>{pct(m.n ? catalogo.historial / m.n : 0)}</Td>
                            </tr>
                          )}
                          <tr>
                            <Td style={{ fontWeight: 700 }}>Total</Td>
                            <Td style={{ fontWeight: 700 }}>{nf.format(catalogo.ganados + catalogo.historial)}</Td>
                            <Td style={{ fontWeight: 700 }}>{nf.format(catalogo.descargados)}</Td>
                            <Td style={{ fontWeight: 700 }}>
                              {nf.format(catalogo.personas)} personas
                            </Td>
                          </tr>
                        </tbody>
                      </Table>
                    </TableWrap>
                  </PanelBody>
                </Panel>
              )}

                </Grid2>
              )}

              {metas && (
                <Panel>
                  <PanelHead>
                    <div>
                      <h3>{metas.titulo}</h3>
                      <p>Lo comprometido por {etiquetaSegmento(metas.campo, perfilMeta).toLowerCase()} contra los
                        inscritos reales. {nf.format(metas.cumplidas)} de {nf.format(metas.rows.filter(r => r.target).length)} metas
                        cumplidas{metas.fuera ? ` · ${nf.format(metas.fuera)} inscritos fuera de los grupos definidos` : ''}.</p>
                    </div>
                    <Actions><Btn onClick={exportarMetas}>⬇ Descargar</Btn></Actions>
                  </PanelHead>
                  <PanelBody>
                    <TableWrap>
                      <Table>
                        <thead><tr>
                          <Th>{etiquetaSegmento(metas.campo, perfilMeta)}</Th>
                          <Th>Meta</Th><Th>Inscritos</Th><Th>Cumplimiento</Th><Th>Faltan</Th><Th>Certificados</Th>
                        </tr></thead>
                        <tbody>
                          {metas.rows.map(r => (
                            <tr key={r.id}>
                              <Td title={r.valores.join(', ')}>{r.etiqueta || r.valores.join(', ') || '—'}</Td>
                              <Td>{r.target ? nf.format(r.target) : '—'}</Td>
                              <Td style={{ fontWeight: 700 }}>{nf.format(r.inscritos)}</Td>
                              <Td>
                                {r.pct == null ? '—' : (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Bar style={{ flex: 1, minWidth: 70 }}>
                                      <Fill style={{ width: `${Math.min(100, r.pct * 100)}%`,
                                                     background: r.pct >= 1 ? tema.ok : r.pct >= 0.6 ? tema.warn : tema.bad }} />
                                    </Bar>
                                    <strong style={{ color: r.pct >= 1 ? tema.ok : r.pct >= 0.6 ? tema.warn : tema.bad }}>
                                      {pct(r.pct)}
                                    </strong>
                                  </div>
                                )}
                              </Td>
                              <Td>{r.target ? (r.falta ? nf.format(r.falta) : '✓') : '—'}</Td>
                              <Td>{nf.format(r.certificados)}</Td>
                            </tr>
                          ))}
                          {metas.fuera > 0 && (
                            <tr>
                              <Td style={{ color: tema.muted }}>Sin meta definida</Td>
                              <Td>—</Td>
                              <Td>{nf.format(metas.fuera)}</Td>
                              <Td>—</Td><Td>—</Td>
                              <Td>{nf.format(metas.fueraCert)}</Td>
                            </tr>
                          )}
                          <tr>
                            <Td style={{ fontWeight: 700 }}>Total</Td>
                            <Td style={{ fontWeight: 700 }}>{nf.format(metas.target)}</Td>
                            <Td style={{ fontWeight: 700 }}>{nf.format(metas.inscritos)}</Td>
                            <Td style={{ fontWeight: 700 }}>{metas.pct == null ? '—' : pct(metas.pct)}</Td>
                            <Td style={{ fontWeight: 700 }}>{Math.max(0, metas.target - metas.inscritos) || '✓'}</Td>
                            <Td style={{ fontWeight: 700 }}>{nf.format(metas.certificados)}</Td>
                          </tr>
                        </tbody>
                      </Table>
                    </TableWrap>
                  </PanelBody>
                </Panel>
              )}

              {data.comparar_periodos !== false && (
              <Panel>
                <PanelHead>
                  <h3>Comparar periodos</h3>
                  <p>Elige un periodo de registro para contrastarlo con la selección actual.</p>
                </PanelHead>
                <PanelBody>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
                    <Field style={{ minWidth: 170 }}><span>Periodo desde</span>
                      <Input type="date" value={compara.desde} onChange={e => setCompara(c => ({ ...c, desde: e.target.value }))} />
                    </Field>
                    <Field style={{ minWidth: 170 }}><span>Periodo hasta</span>
                      <Input type="date" value={compara.hasta} onChange={e => setCompara(c => ({ ...c, hasta: e.target.value }))} />
                    </Field>
                    <Ghost onClick={() => setCompara({ desde: '', hasta: '' })}>Quitar comparación</Ghost>
                  </div>
                  {comparacion ? (
                    <MiniGrid style={{ marginTop: 14 }}>
                      {comparacion.map(([label, actual, previo, esPct]) => {
                        const delta = actual - previo;
                        return (
                          <Mini key={label}>
                            <span>{label}</span>
                            <strong style={{ fontSize: '1.35rem' }}>{esPct ? pct(actual, 1) : nf.format(actual)}</strong>
                            <p style={{ color: delta >= 0 ? tema.ok : tema.bad }}>
                              {delta >= 0 ? '+' : ''}{esPct ? `${df.format(delta * 100)} pp` : nf.format(delta)} frente al periodo elegido
                            </p>
                          </Mini>
                        );
                      })}
                    </MiniGrid>
                  ) : <Empty style={{ marginTop: 14 }}>Selecciona las dos fechas para ver la comparación.</Empty>}
                </PanelBody>
              </Panel>
              )}
            </>
          )}

          {/* ══ COBERTURA ══ */}
          {tipoActual === 'cobertura' && (() => {
            // Un panel por cada campo de segmento configurado en esta pestaña.
            const segmentos = (pestanaActual.segmentos || [])
              .filter(c => c !== 'convenio' || data.tiene_convenios)
              .map(campo => ({
                campo,
                etiqueta: etiquetaSegmento(campo, perfilMeta),
                filas: agrupar(filtrados, campo, valorSegmento),
              }));
            const mejorTasa = (arr) => [...arr].filter(x => x.registrados >= 5).sort((a, b) => b.tasa - a.tasa)[0] || arr[0];
            const corta = (t) => (t.length > 34 ? `${t.slice(0, 32)}…` : t);

            // Bloques adicionales: todos miran el campo principal (el primer segmento).
            const bloques = pestanaActual.bloques || {};
            const principal = segmentos[0] || null;
            const valorDe = (e) => (principal ? (valorSegmento(e, principal.campo) || SIN_DATO) : SIN_DATO);
            const conjunto = (vals) => new Set((vals || []).map(claveSegmento));

            const tarjetas = (bloques.resumen?.activo && principal) ? (() => {
              const lista = bloques.resumen.tarjetas || [];
              const usados = new Set(lista.filter(t => !t.resto).flatMap(t => t.valores.map(claveSegmento)));
              return lista.map(t => {
                const vals = conjunto(t.valores);
                const n = filtrados.filter(e => {
                  const k = claveSegmento(valorDe(e));
                  return t.resto ? !usados.has(k) : vals.has(k);
                }).length;
                return { ...t, n };
              });
            })() : [];

            // Asignados = quienes no tienen ninguno de los valores excluidos (p. ej. «No Panel»).
            // Avance y certificados, sobre todos o solo sobre los asignados según la configuración.
            const fueraInd = conjunto(bloques.indicadores?.excluir);
            const hayAsignacion = fueraInd.size > 0;
            const asignados = filtrados.filter(e => !fueraInd.has(claveSegmento(valorDe(e))));
            const baseInd = hayAsignacion && bloques.indicadores?.solo_asignados ? asignados : filtrados;
            const ind = {
              total: filtrados.length,
              asignados: asignados.length,
              n: baseInd.length,
              conAvance: baseInd.filter(e => (e.avance || 0) > 0).length,
              avance: baseInd.length ? baseInd.reduce((acc, e) => acc + (e.avance || 0), 0) / baseInd.length : 0,
              cert: baseInd.filter(e => e.cumple).length,
            };
            const sobre = baseInd === asignados && hayAsignacion ? 'de los asignados' : 'del total';

            const fueraCon = conjunto(bloques.contacto?.excluir);
            const campoUbic = bloques.contacto?.ubicacion || '';
            const leerOrden = (e, campo) => {
              if (campo === 'segmento') return claveSegmento(valorDe(e));
              if (campo === 'ubicacion') return claveSegmento(campoUbic ? valorSegmento(e, campoUbic) : '');
              // Quien nunca entró va después de los inactivos (como en el modelo): primero
              // a quien empezó y lleva más días sin volver.
              if (campo === 'dias_inactividad') return e.dias_inactividad ?? -1;
              if (campo === 'nombre' || campo === 'estado') return claveSegmento(e[campo]);
              return e[campo] ?? 0;
            };
            const contactos = bloques.contacto?.activo
              ? filtrados.filter(e => !fueraCon.has(claveSegmento(valorDe(e))))
                .sort((a, b) => {
                  const x = leerOrden(a, contactoOrden.campo); const y = leerOrden(b, contactoOrden.campo);
                  return (x > y ? 1 : x < y ? -1 : 0) * contactoOrden.dir;
                })
              : [];
            const POR_PAGINA = 25;
            const paginas = Math.max(1, Math.ceil(contactos.length / POR_PAGINA));
            const pag = Math.min(contactoOrden.page, paginas - 1);
            const ordenarContacto = (campo) => setContactoOrden(o => ({
              campo, page: 0, dir: o.campo === campo ? -o.dir : (campo === 'dias_inactividad' || campo === 'avance' ? -1 : 1),
            }));
            const descargarContacto = () => bajarCSV(
              nombreArchivo(`contacto_${pestanaActual.titulo}`),
              ['Estudiante', 'Especialidad', 'Correo', 'Teléfono', principal ? principal.etiqueta : 'Segmento',
                ...(campoUbic ? [etiquetaSegmento(campoUbic, perfilMeta)] : []), 'Estado', 'Avance %', 'Días inactivo'],
              contactos.map(e => [e.nombre, e.especialidad || '', e.email, e.telefono || '', valorDe(e),
                ...(campoUbic ? [valorSegmento(e, campoUbic) || ''] : []), e.estado, e.avance ?? 0,
                e.dias_inactividad ?? 'Sin actividad'])
            );
            const flecha = (campo) => (contactoOrden.campo === campo ? (contactoOrden.dir === 1 ? ' ↑' : ' ↓') : ' ↕');

            return (
              <>
                <Heading>
                  <div><Eyebrow>{pestanaActual.titulo}</Eyebrow><H2>Volumen y certificación por segmento</H2></div>
                  <Note>Cada barra compara registrados contra certificados. La tasa se calcula dentro del segmento.</Note>
                </Heading>

                {tarjetas.length > 0 && (
                  <KpiGrid>
                    {tarjetas.map((t, i) => (
                      <KpiCard key={`${t.titulo}${i}`} $tone={[tema.serieA, tema.serieC, tema.warn, tema.ok][i % 4]}>
                        <span>{t.titulo}</span><strong>{nf.format(t.n)}</strong>
                        <small>{t.descripcion || `${pct(filtrados.length ? t.n / filtrados.length : 0)} de los estudiantes`}</small>
                      </KpiCard>
                    ))}
                  </KpiGrid>
                )}

                {!segmentos.length ? (
                  <Panel><PanelBody><Empty>Esta pestaña no tiene segmentos configurados.</Empty></PanelBody></Panel>
                ) : (
                  <Grid2>
                    {segmentos.map(sg => (
                      <Panel key={sg.campo}>
                        <PanelHead><h3>Por {sg.etiqueta.charAt(0).toLowerCase() + sg.etiqueta.slice(1)}</h3></PanelHead>
                        <PanelBody><BarrasResultado rows={sg.filas} tema={tema} limite={25} /></PanelBody>
                      </Panel>
                    ))}
                    {bloques.indicadores?.activo && (
                      <Panel>
                        <PanelHead>
                          <h3>{bloques.indicadores.titulo}</h3>
                          {hayAsignacion && (
                            <p>
                              Asignados: todos menos {bloques.indicadores.excluir.join(', ')}.
                              {bloques.indicadores.solo_asignados ? ' Avance y certificados, solo de los asignados.' : ''}
                            </p>
                          )}
                        </PanelHead>
                        <PanelBody>
                          <MiniGrid>
                            {hayAsignacion ? (
                              <Mini><span>Estudiantes asignados</span><strong>{nf.format(ind.asignados)}</strong><p>{pct(ind.total ? ind.asignados / ind.total : 0)} de {nf.format(ind.total)} estudiantes</p></Mini>
                            ) : (
                              <Mini><span>Estudiantes</span><strong>{nf.format(ind.total)}</strong><p>Con los filtros actuales</p></Mini>
                            )}
                            <Mini><span>Con avance</span><strong>{nf.format(ind.conAvance)}</strong><p>{pct(ind.n ? ind.conAvance / ind.n : 0)} {sobre} ya empezó</p></Mini>
                            <Mini><span>Avance promedio</span><strong>{pct(ind.avance / 100, 1)}</strong><p>Promedio {sobre}</p></Mini>
                            <Mini><span>Certificados</span><strong>{nf.format(ind.cert)}</strong><p>{pct(ind.n ? ind.cert / ind.n : 0)} {sobre}</p></Mini>
                          </MiniGrid>
                        </PanelBody>
                      </Panel>
                    )}
                  </Grid2>
                )}

                {segmentos.length > 0 && !bloques.indicadores?.activo && (
                  <MiniGrid>
                    {segmentos.slice(0, 2).flatMap(sg => [
                      [`Mayor volumen · ${corta(sg.etiqueta)}`, sg.filas[0]],
                      [`Mejor tasa · ${corta(sg.etiqueta)}`, mejorTasa(sg.filas)],
                    ]).map(([label, r]) => (
                      <Mini key={label}>
                        <span>{label}</span>
                        <strong>{r?.name || 'Sin datos'}</strong>
                        <p>{r ? `${nf.format(r.registrados)} registrados · ${pct(r.tasa)} certificados` : '—'}</p>
                      </Mini>
                    ))}
                  </MiniGrid>
                )}

                {bloques.contacto?.activo && (
                  <Panel>
                    <PanelHead>
                      <h3>{bloques.contacto.titulo}</h3>
                      <p>Ordenada por días sin actividad. Haz clic en un encabezado para cambiar el orden.</p>
                    </PanelHead>
                    <TableTools>
                      <span>{nf.format(contactos.length)} estudiantes</span>
                      <Btn onClick={descargarContacto} disabled={!contactos.length}>Descargar tabla</Btn>
                    </TableTools>
                    <TableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <Th $sortable onClick={() => ordenarContacto('nombre')}>Estudiante{flecha('nombre')}</Th>
                            <Th>Contacto</Th>
                            {principal && <Th $sortable onClick={() => ordenarContacto('segmento')}>{principal.etiqueta}{flecha('segmento')}</Th>}
                            {campoUbic && <Th $sortable onClick={() => ordenarContacto('ubicacion')}>{etiquetaSegmento(campoUbic, perfilMeta)}{flecha('ubicacion')}</Th>}
                            <Th $sortable onClick={() => ordenarContacto('estado')}>Estado{flecha('estado')}</Th>
                            <Th $sortable onClick={() => ordenarContacto('avance')}>Avance{flecha('avance')}</Th>
                            <Th $sortable onClick={() => ordenarContacto('dias_inactividad')}>Inactividad{flecha('dias_inactividad')}</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {contactos.slice(pag * POR_PAGINA, (pag + 1) * POR_PAGINA).map((e, i) => (
                            <tr key={`${e.email}${i}`}>
                              <Td>
                                <div style={{ color: tema.text, fontWeight: 600 }}>{e.nombre}</div>
                                {e.especialidad && <small style={{ opacity: 0.75 }}>{e.especialidad}</small>}
                              </Td>
                              <Td>
                                <div>{e.email}</div>
                                {e.telefono && <small style={{ opacity: 0.75 }}>{e.telefono}</small>}
                              </Td>
                              {principal && <Td>{valorDe(e)}</Td>}
                              {campoUbic && <Td>{valorSegmento(e, campoUbic) || '—'}</Td>}
                              <Td>
                                <Badge $tone={e.estado === 'Certificado' ? 'ok' : e.estado === 'En progreso' ? 'warn' : 'muted'}>{e.estado}</Badge>
                              </Td>
                              <Td style={{ color: tonoPorPct(tema, e.avance || 0), fontWeight: 700 }}>{e.avance ?? 0}%</Td>
                              <Td>{e.dias_inactividad == null ? 'Sin actividad' : `${nf.format(e.dias_inactividad)} días`}</Td>
                            </tr>
                          ))}
                          {contactos.length === 0 && (
                            <tr><Td colSpan={7} style={{ textAlign: 'center' }}>Sin estudiantes con estos filtros.</Td></tr>
                          )}
                        </tbody>
                      </Table>
                    </TableWrap>
                    {paginas > 1 && (
                      <Pager style={{ padding: '12px 0' }}>
                        <Ghost disabled={pag === 0} onClick={() => setContactoOrden(o => ({ ...o, page: Math.max(0, pag - 1) }))}>← Anterior</Ghost>
                        <span>Página {pag + 1} de {paginas}</span>
                        <Ghost disabled={pag >= paginas - 1} onClick={() => setContactoOrden(o => ({ ...o, page: Math.min(paginas - 1, pag + 1) }))}>Siguiente →</Ghost>
                      </Pager>
                    )}
                  </Panel>
                )}
              </>
            );
          })()}

          {/* ══ JOURNEY ══ */}
          {tipoActual === 'journey' && (() => {
            const paneles = normalizarPanelesJourney(pestanaActual?.paneles);
            const segJourney = (pestanaActual?.segmentos || [])
              .filter(c => c !== 'convenio' || data.tiene_convenios)
              .map(campo => ({ campo, etiqueta: etiquetaSegmento(campo, perfilMeta), filas: agrupar(filtrados, campo, valorSegmento) }));
            const hayAnalisis = (paneles.modulos && modAgg.length) || (paneles.evaluaciones && evalAgg.length) || (paneles.requisitos && reqAgg.length);
            return (
            <>
              <Heading>
                <div>
                  <Eyebrow>Journey académico</Eyebrow>
                  <H2>{journeyRutas.filas.length > 1 ? 'Ubicación actual por audiencia y módulo' : 'Ubicación actual por módulo'}</H2>
                </div>
                <Note>
                  Cada estudiante cuenta en el primer módulo de su ruta que aún no termina.
                  {journeyRutas.hayEspecializacion && ` Los módulos comunes a todas las rutas van en azul; los de especialización, en verde.`}
                </Note>
              </Heading>

              {journeyRutas.filas.map(r => (
                <Panel key={r.key}>
                  <PanelHead>
                    <h3>{r.nombre || 'Todos los estudiantes'}</h3>
                    <p>{r.docs.length} módulos · {nf.format(r.estudiantes.length)} estudiantes</p>
                  </PanelHead>
                  <PanelBody>
                    <Cubes>
                      {r.celdas.map(c => (
                        <Cube key={c.label} $kind={c.kind} title={c.titulo ? `${c.label}: ${c.titulo}` : undefined}>
                          <span>{c.label}</span>
                          <strong>{nf.format(c.n)}</strong>
                          <small>{pct(r.estudiantes.length ? c.n / r.estudiantes.length : 0)}</small>
                        </Cube>
                      ))}
                    </Cubes>
                    {r.docs.length > 0 && (
                      <div style={{ marginTop: 10, fontSize: '0.72rem', color: tema.muted, lineHeight: 1.6 }}>
                        {r.celdas.filter(c => c.titulo).map(c => (
                          <span key={c.label} style={{ marginRight: 14, whiteSpace: 'nowrap', display: 'inline-block' }}>
                            <b style={{ color: tema.text2 }}>{c.label}</b> {c.titulo}
                          </span>
                        ))}
                      </div>
                    )}
                  </PanelBody>
                </Panel>
              ))}
              {journeyRutas.sinRuta > 0 && (
                <Note style={{ margin: '-4px 0 14px' }}>
                  {nf.format(journeyRutas.sinRuta)} estudiante{journeyRutas.sinRuta === 1 ? '' : 's'} sin ruta de certificación no aparece{journeyRutas.sinRuta === 1 ? '' : 'n'} arriba (ver Calidad de datos).
                </Note>
              )}

              {segJourney.map(sg => (
                <Panel key={sg.campo}>
                  <PanelHead>
                    <h3>Registrados y certificados por {sg.etiqueta.charAt(0).toLowerCase() + sg.etiqueta.slice(1)}</h3>
                    <p>Ordenados por estudiantes registrados.</p>
                  </PanelHead>
                  <PanelBody><BarrasResultado rows={sg.filas} tema={tema} limite={18} /></PanelBody>
                </Panel>
              ))}

              {paneles.distribucion && hayAnalisis && filtrados.length > 0 && (
                <Panel>
                  <PanelHead>
                    <h3>Distribución por puntuación — {pieTitulo}</h3>
                    <p>Elige un módulo, una evaluación o un requisito abajo para analizarlo. Clic en un segmento para filtrar las tablas.</p>
                  </PanelHead>
                  <PanelBody style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ width: 210, height: 210 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={84} paddingAngle={2}
                               onClick={(_, idx) => { const b = pieData[idx]?.name; setSegFilter(segFilter === b ? null : b); }}>
                            {pieData.map(d => (
                              <Cell key={d.name} fill={d.color} stroke={segFilter === d.name ? tema.text : 'none'} strokeWidth={2}
                                    opacity={segFilter && segFilter !== d.name ? 0.35 : 1} style={{ cursor: 'pointer' }} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ background: tema.card, border: `1px solid ${tema.line}`, borderRadius: 10, color: tema.text }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <Chips>
                      {pieData.map(d => (
                        <Chip key={d.name} $active={segFilter === d.name} onClick={() => setSegFilter(segFilter === d.name ? null : d.name)}>
                          <Dot $c={d.color} />{d.name} · {d.value}
                        </Chip>
                      ))}
                    </Chips>
                  </PanelBody>
                </Panel>
              )}

              {paneles.modulos && modAgg.length > 0 && (
                <Panel>
                  <PanelHead><h3>Progreso de contenido por módulo</h3><p>{modAgg.length} módulos · clic para analizar uno.</p></PanelHead>
                  <PanelBody>
                    <CardGrid>
                      {modAgg.map(mo => {
                        const activo = focus?.tipo === 'mod' && focus.key === mo.key;
                        return (
                          <CardBtn key={mo.key} $active={activo}
                                   onClick={() => setFocus(activo ? null : { tipo: 'mod', key: mo.key, titulo: mo.titulo })}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>{mo.titulo}</div>
                            <div style={{ fontSize: '0.76rem', color: tema.muted }}>{mo.compl}/{mo.total} completaron</div>
                            <Bar><Fill style={{ width: `${mo.avg}%`, background: tonoPorPct(tema, mo.avg) }} /></Bar>
                            <div style={{ fontSize: '0.76rem', color: tema.text2, marginTop: 4 }}>{mo.avg}% promedio</div>
                          </CardBtn>
                        );
                      })}
                    </CardGrid>
                  </PanelBody>
                </Panel>
              )}

              {paneles.evaluaciones && evalAgg.length > 0 && (
                <Panel>
                  <PanelHead><h3>Calificaciones de evaluación</h3><p>Promedio de quienes presentaron.</p></PanelHead>
                  <PanelBody>
                    <CardGrid>
                      {evalAgg.map(mo => {
                        const activo = focus?.tipo === 'eval' && focus.key === mo.key;
                        return (
                          <CardBtn key={mo.key} $active={activo}
                                   onClick={() => setFocus(activo ? null : { tipo: 'eval', key: mo.key, titulo: `Eval: ${mo.titulo}` })}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>{mo.titulo}</div>
                            <div style={{ fontSize: '0.76rem', color: tema.muted }}>{mo.aprob}/{mo.n} aprobados · {mo.n} presentaron</div>
                            <Bar><Fill style={{ width: `${mo.avg}%`, background: tonoPorPct(tema, mo.avg) }} /></Bar>
                            <div style={{ fontSize: '0.76rem', color: tema.text2, marginTop: 4 }}>{mo.avg}% promedio · {mo.aprobPct}% aprobación</div>
                          </CardBtn>
                        );
                      })}
                    </CardGrid>
                  </PanelBody>
                </Panel>
              )}

              {paneles.requisitos && reqAgg.length > 0 && (
                <Panel>
                  <PanelHead><h3>Requisitos de certificación</h3><p>{reqAgg.length} requisitos del programa.</p></PanelHead>
                  <PanelBody>
                    <CardGrid>
                      {reqAgg.map(r => {
                        const activo = focus?.tipo === 'req' && focus.key === r.key;
                        return (
                          <CardBtn key={r.key} $active={activo}
                                   onClick={() => setFocus(activo ? null : { tipo: 'req', key: r.key, titulo: r.titulo })}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>{r.titulo}</div>
                            <div style={{ fontSize: '0.76rem', color: tema.muted }}>{r.compl}/{r.total} cumplen</div>
                            <Bar><Fill style={{ width: `${r.pct}%`, background: tonoPorPct(tema, r.pct) }} /></Bar>
                            <div style={{ fontSize: '0.76rem', color: tema.text2, marginTop: 4 }}>{r.pct}%</div>
                          </CardBtn>
                        );
                      })}
                    </CardGrid>
                  </PanelBody>
                </Panel>
              )}
            </>
            );
          })()}

          {/* ══ SATISFACCIÓN ══ */}
          {tipoActual === 'satisfaccion' && (
            encuesta && encuesta.total > 0 ? (
              <>
                <Heading>
                  <div><Eyebrow>Satisfacción e impacto</Eyebrow><H2>Resultados de todas las preguntas</H2></div>
                  <Actions>
                    <Ghost onClick={() => { setVerRespuestas(v => !v); setEncPage(0); }}>
                      {verRespuestas ? '▲ Ocultar quién respondió' : '▼ Ver quién respondió'}
                    </Ghost>
                    <Btn onClick={exportarEncuesta}>⬇ Descargar respuestas completas</Btn>
                  </Actions>
                </Heading>
                <Note style={{ marginBottom: 12 }}>
                  {encuesta.filtrando
                    ? `${nf.format(encuesta.resp.length)} de ${nf.format(encuesta.total)} respuestas, según los filtros.`
                    : `${nf.format(encuesta.total)} respuestas${data.scope?.tipo !== 'all' ? ` (${data.scope?.label})` : ''}.`}
                </Note>

                {encuesta.resp.length === 0 ? (
                  <Empty>Ningún estudiante de los filtros respondió la encuesta.</Empty>
                ) : (
                  <>
                    <SurveyGrid>
                      {encuesta.tarjetas.map((t, i) => (
                        <SurveyCard key={t.id}>
                          <span>Pregunta {i + 1}</span>
                          <h3 title={t.titulo}>{t.titulo}</h3>
                          <strong className={t.largo ? 'largo' : undefined}>{t.valor}</strong>
                          {t.sub && <small>{t.sub}</small>}
                        </SurveyCard>
                      ))}
                    </SurveyGrid>

                    {encuesta.aspectos.length > 1 && (
                      <Panel>
                        <PanelHead>
                          <h3>Satisfacción por aspecto</h3>
                          <p>Promedio y porcentaje de respuestas satisfechas (las dos notas más altas de la escala).</p>
                        </PanelHead>
                        <PanelBody>
                          <AspectGrid>
                            {encuesta.aspectos.map(a => (
                              <Aspect key={a.texto}>
                                <h4>{a.texto}</h4>
                                <strong>{a.n ? `${df.format(a.prom)}/${a.max}` : '—'}</strong>
                                <span>{a.n ? `${pct(a.sat)} satisfechos` : 'Sin respuestas'}</span>
                                <Progress><i style={{ width: `${(a.sat || 0) * 100}%` }} /></Progress>
                                <small>{nf.format(a.n)} respuestas</small>
                              </Aspect>
                            ))}
                          </AspectGrid>
                        </PanelBody>
                      </Panel>
                    )}

                    {(encuesta.opciones.length > 0 || encuesta.abiertas.some(x => x.temas.length)) && (
                      <Grid2 style={{ marginBottom: 14 }}>
                        {encuesta.opciones.map(o => {
                          const max = Math.max(1, ...o.filas.map(r => r.value));
                          return (
                            <Panel key={o.id} style={{ marginBottom: 0 }}>
                              <PanelHead><h3>{o.titulo}</h3></PanelHead>
                              <PanelBody>
                                {o.filas.length ? (
                                  <Bars>
                                    {o.filas.map(r => (
                                      <BarRow key={r.name}>
                                        <span title={r.name}>{r.name}</span>
                                        <Track><FillA style={{ width: `${(r.value / max) * 100}%` }} /></Track>
                                        <BarVal>{nf.format(r.value)}<em>{pct(r.value / Math.max(1, o.n))}</em></BarVal>
                                      </BarRow>
                                    ))}
                                  </Bars>
                                ) : <Empty>Sin respuestas.</Empty>}
                              </PanelBody>
                            </Panel>
                          );
                        })}
                        {encuesta.abiertas.filter(x => x.temas.length).map(x => {
                          const max = Math.max(1, ...x.temas.map(r => r.value));
                          return (
                            <Panel key={x.id} style={{ marginBottom: 0 }}>
                              <PanelHead>
                                <h3>Temas cualitativos</h3>
                                <p>{encuesta.abiertas.length > 1 ? `${x.titulo}. ` : ''}Pulsa un tema para leer sus comentarios.</p>
                              </PanelHead>
                              <PanelBody>
                                <Bars>
                                  {x.temas.map(r => {
                                    const on = comentFiltro?.tipo === 'tema' && comentFiltro.valor === r.name && comentFiltro.pregunta === x.id;
                                    return (
                                      <BarBtn key={r.name} $on={on} aria-pressed={on}
                                              onClick={() => { setComentMax(20); setComentFiltro(on ? null : { tipo: 'tema', valor: r.name, pregunta: x.id }); }}>
                                        <span title={r.name}>{r.name}</span>
                                        <Track><FillA style={{ width: `${(r.value / max) * 100}%`, background: tema.serieC }} /></Track>
                                        <BarVal>{nf.format(r.value)}</BarVal>
                                      </BarBtn>
                                    );
                                  })}
                                </Bars>
                              </PanelBody>
                            </Panel>
                          );
                        })}
                      </Grid2>
                    )}

                    {encuesta.nClasificados > 0 && (
                      <Panel>
                        <PanelHead>
                          <h3>Sentimiento de las respuestas</h3>
                          <p>Sobre {nf.format(encuesta.nClasificados)} comentarios abiertos. Pulsa uno para leerlos.</p>
                        </PanelHead>
                        <PanelBody>
                          <Sentiment>
                            {encuesta.sentimientos.map(x => {
                              const on = comentFiltro?.tipo === 'sentimiento' && comentFiltro.valor === x.name;
                              return (
                                <button key={x.name} aria-pressed={on}
                                        onClick={() => { setComentMax(20); setComentFiltro(on ? null : { tipo: 'sentimiento', valor: x.name }); }}>
                                  <span>{x.name}</span>
                                  <strong>{nf.format(x.value)}</strong>
                                  <small>{pct(x.value / Math.max(1, encuesta.nClasificados))}</small>
                                </button>
                              );
                            })}
                          </Sentiment>
                        </PanelBody>
                      </Panel>
                    )}

                    {encuesta.abiertas.map(x => {
                      if (!x.comentarios.length) return null;
                      const f = comentFiltro;
                      const lista = x.comentarios.filter(c => {
                        if (!f) return true;
                        if (f.tipo === 'tema') return f.pregunta === x.id && c.a?.tema === f.valor;
                        return c.a?.sentimiento === f.valor;
                      });
                      if (f?.tipo === 'tema' && f.pregunta !== x.id) return null;
                      const colorSent = { Positivo: tema.ok, Neutral: tema.warn, Negativo: tema.bad };
                      return (
                        <Panel key={`c_${x.id}`}>
                          <PanelHead>
                            <h3>{x.titulo}</h3>
                            <p>
                              {f ? `${nf.format(lista.length)} comentarios · ${f.valor}. ` : `${nf.format(lista.length)} comentarios. `}
                              {f && <Ghost style={{ padding: '2px 8px', fontSize: '0.74rem' }} onClick={() => setComentFiltro(null)}>Ver todos</Ghost>}
                            </p>
                          </PanelHead>
                          <PanelBody style={{ paddingTop: 4 }}>
                            {lista.slice(0, comentMax).map((c, i) => (
                              <Comentario key={i}>
                                <p>{c.texto}</p>
                                <small>
                                  {c.a && <><Dot $c={colorSent[c.a.sentimiento]} style={{ marginRight: 5 }} />{c.a.sentimiento} · {c.a.tema} · </>}
                                  {c.nombre}
                                </small>
                              </Comentario>
                            ))}
                            {lista.length > comentMax && (
                              <Ghost style={{ marginTop: 10 }} onClick={() => setComentMax(n => n + 40)}>
                                Ver más ({nf.format(lista.length - comentMax)} restantes)
                              </Ghost>
                            )}
                          </PanelBody>
                        </Panel>
                      );
                    })}
                  </>
                )}

                {verRespuestas && encuesta.resp.length > 0 && (() => {
                  const resp = encuesta.resp;
                  const PER = 25;
                  const pages = Math.max(1, Math.ceil(resp.length / PER));
                  const pg = Math.min(encPage, pages - 1);
                  const slice = resp.slice(pg * PER, pg * PER + PER);
                  const fmt = (c, r) => {
                    const v = c.valor(r);
                    if (v === undefined || v === null || v === '') return '—';
                    return c.sufijo && !Number.isNaN(Number(v)) ? `${v}${c.sufijo}` : String(v);
                  };
                  return (
                    <div style={{ marginTop: 16 }}>
                      <TableWrap>
                        <Table>
                          <thead>
                            <tr>
                              <Th>Estudiante</Th><Th>Correo</Th>
                              {data.tiene_convenios && <Th>Convenio</Th>}
                              {columnasEncuesta.map(c => <Th key={c.key}>{c.titulo}</Th>)}
                              <Th>Fecha</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {slice.map((r, i) => (
                              <tr key={i}>
                                <Td style={{ color: tema.text, fontWeight: 600 }}>{r.nombre}</Td>
                                <Td>{r.email}</Td>
                                {data.tiene_convenios && <Td>{r.convenio || '—'}</Td>}
                                {columnasEncuesta.map(c => (
                                  <Td key={c.key} $wrap style={{ maxWidth: c.largo ? 320 : 180 }}>{fmt(c, r)}</Td>
                                ))}
                                <Td>{fmtFecha(r.fecha)}</Td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableWrap>
                      {pages > 1 && (
                        <Pager>
                          <Ghost disabled={pg === 0} onClick={() => setEncPage(x => Math.max(0, x - 1))}>← Anterior</Ghost>
                          <span>Página {pg + 1} de {pages} · {resp.length} respuestas</span>
                          <Ghost disabled={pg >= pages - 1} onClick={() => setEncPage(x => Math.min(pages - 1, x + 1))}>Siguiente →</Ghost>
                        </Pager>
                      )}
                    </div>
                  );
                })()}
              </>
            ) : (
              <>
                <Heading><div><Eyebrow>Satisfacción</Eyebrow><H2>Sin respuestas todavía</H2></div></Heading>
                <Empty>Este programa aún no tiene encuesta habilitada o nadie la ha respondido.</Empty>
              </>
            )
          )}

          {/* ══ ESTUDIANTES ══ */}
          {tipoActual === 'estudiantes' && (
            <>
              <Heading>
                <div><Eyebrow>Estudiantes</Eyebrow><H2>Listado y seguimiento</H2></div>
                <Actions>
                  <Ghost onClick={() => setOrden({ campo: 'dias_inactividad', dir: -1 })}>Priorizar por inactividad</Ghost>
                  <Btn onClick={exportarPerfiles}>⬇ Descargar tabla</Btn>
                </Actions>
              </Heading>

              <Panel>
                <TableTools>
                  <span>{nf.format(ordenados.length)} estudiantes · clic en un encabezado para ordenar</span>
                  <Ghost onClick={exportarCompleto}>⬇ CSV con progreso completo</Ghost>
                </TableTools>
                <TableWrap $max="640px">
                  <Table>
                    <thead>
                      <tr>
                        {perfilMeta.length
                          ? perfilMeta.map((c, i) => (
                              <Th key={`p${c.key}`} title={c.titulo || undefined} $sortable={i === 0}
                                  onClick={i === 0 ? () => ordenarPor('perfil0') : undefined}>
                                {c.etiqueta}{i === 0 ? ' ↕' : ''}
                              </Th>))
                          : <>
                              <Th $sortable onClick={() => ordenarPor('nombre')}>Estudiante ↕</Th>
                              <Th>Correo</Th>
                              <Th $sortable onClick={() => ordenarPor('pais')}>País ↕</Th>
                              <Th>Especialidad</Th>
                            </>}
                        {data.tiene_convenios && !perfilMeta.some(c => c.key === 'convenio') && <Th>Convenio</Th>}
                        <Th $sortable onClick={() => ordenarPor('estado')}>Estado ↕</Th>
                        <Th $sortable onClick={() => ordenarPor(data.catalogo_certificados ? 'certs_cumplidos' : 'avance')}>
                          {data.catalogo_certificados ? 'Cursos certificados' : 'Avance'} ↕
                        </Th>
                        <Th $sortable onClick={() => ordenarPor('fecha_registro')}>Registro ↕</Th>
                        <Th $sortable onClick={() => ordenarPor('dias_inactividad')}>Inactividad ↕</Th>
                        <Th>Certificación</Th>
                        {educare && <Th title="Avance en la plataforma externa (cruce por correo)">Educare</Th>}
                        {learning && <Th title="Avance en learning.ateneo.co (cruce por correo)">Learning</Th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((e, i) => (
                        <tr key={i}>
                          {perfilMeta.length
                            ? perfilMeta.map((c, ci) => (
                                <Td key={`p${c.key}`} style={ci === 0 ? { color: tema.text, fontWeight: 600 } : undefined}>
                                  {e.perfil?.[c.key] || '—'}
                                </Td>))
                            : <>
                                <Td style={{ color: tema.text, fontWeight: 600 }}>{e.nombre}</Td>
                                <Td>{e.email}</Td>
                                <Td>{e.pais || '—'}</Td>
                                <Td>{e.especialidad || '—'}</Td>
                              </>}
                          {data.tiene_convenios && !perfilMeta.some(c => c.key === 'convenio') && <Td>{e.convenio || '—'}</Td>}
                          <Td>
                            <Badge $tone={e.estado === 'Certificado' ? 'ok' : e.estado === 'En progreso' ? 'warn' : 'muted'}>
                              {e.estado}
                            </Badge>
                          </Td>
                          <Td style={{ color: tonoPorPct(tema, e.avance || 0), fontWeight: 700 }}>
                            {e.cert_historial && !(e.certs_keys || []).length
                              ? <>Certificado previo
                                  <small style={{ display: 'block', fontWeight: 400, color: tema.muted }}>antes de la plataforma</small></>
                              : e.certs_total
                              ? <>{nf.format(e.certs_cumplidos ?? 0)} de {nf.format(e.certs_total)}
                                  <small style={{ display: 'block', fontWeight: 400, color: tema.muted }}
                                         title={(e.certs_keys || []).map(k => catalogo?.nombres?.[k] || k).join('\n') || 'Sin certificados'}>
                                    {(e.certs_keys || []).length
                                      ? `certificados · ${nf.format((e.certs_emitidos || []).length)} descargados`
                                      : 'cursos certificados'}
                                  </small></>
                              : `${e.avance ?? 0}%`}
                          </Td>
                          <Td>{fmtFecha(e.fecha_registro)}</Td>
                          <Td>{e.dias_inactividad == null ? 'Sin actividad' : `${nf.format(e.dias_inactividad)} días`}</Td>
                          <Td><Badge $tone={e.cumple ? 'ok' : 'muted'}>{e.cumple ? 'Cumple' : 'Pendiente'}</Badge></Td>
                          {educare && (
                            <Td title={(e.educare?.detalle || []).map(d => `${d.curso}: ${d.estado}`).join('\n') || undefined}>
                              {e.educare
                                ? <>{nf.format(e.educare.completados)} de {nf.format(e.educare.cursos)}
                                    <small style={{ display: 'block', fontWeight: 400, color: tema.muted }}>completados allí</small></>
                                : '—'}
                            </Td>
                          )}
                          {learning && (
                            <Td title={(e.learning?.detalle || []).map(d => `${d.curso}: ${d.progreso}%${d.certificado ? ' · certificado' : ''}`).join('\n') || undefined}>
                              {e.learning
                                ? (e.learning.certificados || e.learning.con_progreso
                                    ? <>{nf.format(e.learning.certificados)} cert.
                                        <small style={{ display: 'block', fontWeight: 400, color: tema.muted }}>
                                          {nf.format(e.learning.con_progreso)} cursos con avance
                                        </small></>
                                    : <>Inscrito
                                        <small style={{ display: 'block', fontWeight: 400, color: tema.muted }}>sin avance allí</small></>)
                                : '—'}
                            </Td>
                          )}
                        </tr>
                      ))}
                      {ordenados.length === 0 && (
                        <tr><Td colSpan={(perfilMeta.length || 4) + 6 + (educare ? 1 : 0) + (learning ? 1 : 0)} style={{ textAlign: 'center' }}>
                          Sin estudiantes con estos filtros.
                        </Td></tr>
                      )}
                    </tbody>
                  </Table>
                </TableWrap>
                {totalPages > 1 && (
                  <Pager style={{ padding: '12px 0' }}>
                    <Ghost disabled={pageSafe === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Anterior</Ghost>
                    <span>Página {pageSafe + 1} de {totalPages} · {nf.format(ordenados.length)} estudiantes</span>
                    <Ghost disabled={pageSafe >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Siguiente →</Ghost>
                  </Pager>
                )}
              </Panel>

              {/* Educare: la otra plataforma del programa. El cruce es por correo, así
                  que hay tres grupos: en las dos, sólo aquí y sólo en Educare. */}
              {educare && (() => {
                const abierto = !!abiertas.educare;
                return (
                  <Panel>
                    <PanelHead style={{ cursor: 'pointer' }} onClick={() => alternar('educare')}>
                      <h3>{abierto ? '▾' : '▸'} Educare · plataforma externa</h3>
                      <p>{nf.format(educare.personas)} personas allí · {nf.format(educare.en_ambas)} en ambas · {nf.format(educare.certificados || 0)} certificados</p>
                    </PanelHead>
                    {abierto && (
                      <>
                        <KpiGrid>
                          <KpiCard $tone={tema.ok}>
                            <span>Certificados allí</span><strong>{nf.format(educare.certificados || 0)}</strong>
                            <small>{nf.format(educare.con_certificado || 0)} personas con al menos uno</small>
                          </KpiCard>
                          <KpiCard $tone={tema.serieA}>
                            <span>En ambas plataformas</span><strong>{nf.format(educare.en_ambas)}</strong>
                            <small>Estudiantes de aquí que también están en Educare</small>
                          </KpiCard>
                          <KpiCard $tone={tema.serieB}>
                            <span>Sólo aquí</span><strong>{nf.format(educare.solo_interna)}</strong>
                            <small>Sin registro en Educare</small>
                          </KpiCard>
                          <KpiCard $tone={tema.warn}>
                            <span>Sólo en Educare</span><strong>{nf.format(educare.solo_externa)}</strong>
                            <small>No tienen cuenta en esta plataforma</small>
                          </KpiCard>
                          <KpiCard $tone={tema.serieC}>
                            <span>Personas en Educare</span><strong>{nf.format(educare.personas)}</strong>
                            <small>Según el último archivo importado</small>
                          </KpiCard>
                        </KpiGrid>
                        <TableWrap>
                          <Table>
                            <thead><tr><Th>Curso en Educare</Th><Th>Inscripciones</Th></tr></thead>
                            <tbody>
                              {educare.por_curso.map((c, i) => (
                                <tr key={`ec${i}`}>
                                  <Td style={{ color: tema.text, fontWeight: 600 }}>{c.curso}</Td>
                                  <Td>{nf.format(c.filas)}</Td>
                                </tr>
                              ))}
                              {educare.por_estado.map((c, i) => (
                                <tr key={`ee${i}`}>
                                  <Td style={{ color: tema.muted }}>Estado: {c.estado}</Td>
                                  <Td>{nf.format(c.filas)}</Td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </TableWrap>
                        {educare.solo_lista.length > 0 && (
                          <>
                            <TableTools>
                              <span>{nf.format(educare.solo_externa)} personas están sólo en Educare</span>
                            </TableTools>
                            <TableWrap $max="420px">
                              <Table>
                                <thead>
                                  <tr><Th>Persona</Th><Th>Correo</Th><Th>Cursos</Th><Th>Completados</Th></tr>
                                </thead>
                                <tbody>
                                  {educare.solo_lista.map((x, i) => (
                                    <tr key={`es${i}`}>
                                      <Td style={{ color: tema.text, fontWeight: 600 }}>{x.alumno || '—'}</Td>
                                      <Td>{x.correo}</Td>
                                      <Td>{nf.format(x.cursos)}</Td>
                                      <Td>{nf.format(x.completados)}</Td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </TableWrap>
                          </>
                        )}
                      </>
                    )}
                  </Panel>
                );
              })()}

              {/* learning.ateneo.co: la plataforma partner. El catálogo son 63 cursos y
                  casi todos vienen en cero, así que se listan sólo los que tienen algo. */}
              {learning && (() => {
                const abierto = !!abiertas.learning;
                return (
                  <Panel>
                    <PanelHead style={{ cursor: 'pointer' }} onClick={() => alternar('learning')}>
                      <h3>{abierto ? '▾' : '▸'} Learning · plataforma partner</h3>
                      <p>{nf.format(learning.personas)} personas allí · {nf.format(learning.en_ambas)} en ambas · {nf.format(learning.certificados)} certificados</p>
                    </PanelHead>
                    {abierto && (
                      <>
                        <KpiGrid>
                          <KpiCard $tone={tema.serieA}>
                            <span>En ambas plataformas</span><strong>{nf.format(learning.en_ambas)}</strong>
                            <small>Estudiantes de aquí que también están en Learning</small>
                          </KpiCard>
                          <KpiCard $tone={tema.serieB}>
                            <span>Sólo aquí</span><strong>{nf.format(learning.solo_interna)}</strong>
                            <small>Sin registro en Learning</small>
                          </KpiCard>
                          <KpiCard $tone={tema.warn}>
                            <span>Sólo en Learning</span><strong>{nf.format(learning.solo_externa)}</strong>
                            <small>No tienen cuenta en esta plataforma</small>
                          </KpiCard>
                          <KpiCard $tone={tema.ok}>
                            <span>Certificados allí</span><strong>{nf.format(learning.certificados)}</strong>
                            <small>{nf.format(learning.con_certificado)} personas · {nf.format(learning.con_progreso)} con avance</small>
                          </KpiCard>
                        </KpiGrid>
                        <TableTools>
                          <span>
                            Catálogo completo: {nf.format(learning.por_curso.length)} cursos
                            y {nf.format(learning.matriculas)} inscripciones
                            {learning.cursos_con_actividad != null
                              ? ` · ${nf.format(learning.cursos_con_actividad)} con alguna actividad`
                              : ''}
                          </span>
                        </TableTools>
                        <TableWrap $max="420px">
                          <Table>
                            <thead>
                              <tr>
                                <Th>Curso en Learning</Th><Th>Inscritos</Th>
                                <Th>Personas con avance</Th><Th>Certificados</Th>
                              </tr>
                            </thead>
                            <tbody>
                              {learning.por_curso.map((c, i) => (
                                <tr key={`lc${i}`}>
                                  <Td style={{ color: c.personas ? tema.text : undefined, fontWeight: c.personas ? 600 : 400 }}>
                                    {c.curso}
                                  </Td>
                                  <Td>{nf.format(c.inscritos ?? 0)}</Td>
                                  <Td>{c.personas ? nf.format(c.personas) : '—'}</Td>
                                  <Td>{c.certificados ? nf.format(c.certificados) : '—'}</Td>
                                </tr>
                              ))}
                              {learning.por_curso.length === 0 && (
                                <tr><Td colSpan={4} style={{ textAlign: 'center' }}>Sin cursos sincronizados.</Td></tr>
                              )}
                            </tbody>
                          </Table>
                        </TableWrap>
                        {learning.solo_lista.length > 0 && (
                          <>
                            <TableTools>
                              <span>{nf.format(learning.solo_externa)} personas están sólo en Learning</span>
                            </TableTools>
                            <TableWrap $max="420px">
                              <Table>
                                <thead>
                                  <tr><Th>Persona</Th><Th>Correo</Th><Th>Cursos</Th><Th>Con avance</Th><Th>Certificados</Th></tr>
                                </thead>
                                <tbody>
                                  {learning.solo_lista.map((x, i) => (
                                    <tr key={`ls${i}`}>
                                      <Td style={{ color: tema.text, fontWeight: 600 }}>{x.alumno || '—'}</Td>
                                      <Td>{x.correo}</Td>
                                      <Td>{nf.format(x.cursos)}</Td>
                                      <Td>{nf.format(x.con_progreso)}</Td>
                                      <Td>{nf.format(x.certificados)}</Td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </TableWrap>
                          </>
                        )}
                      </>
                    )}
                  </Panel>
                );
              })()}

              {/* Cómo van: una tabla por certificado, con solo sus requisitos */}
              {certMeta.length > 0
                ? certMeta.map(c => {
                    const suyos = visibles.filter(e => e.cert_key === c.key);
                    const cumplen = suyos.filter(e => e.cumple).length;
                    const p = suyos.length ? Math.round(cumplen * 100 / suyos.length) : 0;
                    const docs = c.documentos || [];
                    const mods = modMeta.filter(x => docs.includes(x.documento_id));
                    const evs = evalMeta.filter(x => docs.includes(x.documento_id));
                    const abierto = !!abiertas[`cert_${c.key}`];
                    return (
                      <Panel key={c.key}>
                        <PanelHead style={{ cursor: 'pointer' }} onClick={() => alternar(`cert_${c.key}`)}>
                          <h3>{abierto ? '▾' : '▸'} {c.nombre}</h3>
                          <p>{cumplen} de {suyos.length} certificados ({p}%) · {c.requisitos.length} requisitos</p>
                        </PanelHead>
                        {abierto && (
                          <>
                            <TableTools>
                              <span>{suyos.length} estudiantes de este perfil</span>
                              <Ghost onClick={() => exportarCertificado(c, suyos, mods, evs)}>⬇ CSV de {c.nombre}</Ghost>
                            </TableTools>
                            <TableWrap>
                              <Table>
                                <thead>
                                  <tr>
                                    <Th>Estudiante</Th><Th>Correo</Th>
                                    {mods.map(x => <Th key={`cm${x.documento_id}`}>{x.titulo}</Th>)}
                                    {evs.map(x => <Th key={`ce${x.documento_id}`}>Eval: {x.titulo}</Th>)}
                                    <Th>Certificación</Th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {suyos.slice(0, VER_POR_CERT).map((e, i) => (
                                    <tr key={i}>
                                      <Td style={{ color: tema.text, fontWeight: 600 }}>{e.nombre}</Td>
                                      <Td>{e.email}</Td>
                                      {mods.map(x => {
                                        const md = e.mod?.[String(x.documento_id)] || { pct: 0 };
                                        return <Td key={`cm${x.documento_id}`} style={{ color: tonoPorPct(tema, md.pct), fontWeight: 700 }}>{md.pct}%</Td>;
                                      })}
                                      {evs.map(x => {
                                        const ev = e.eval?.[String(x.documento_id)];
                                        return (
                                          <Td key={`ce${x.documento_id}`} style={{ fontWeight: 700, color: ev ? (ev.aprobado ? tema.ok : tonoPorPct(tema, ev.pct)) : tema.faint }}>
                                            {ev ? `${ev.pct}%${ev.aprobado ? ' ✓' : ''}` : '—'}
                                          </Td>
                                        );
                                      })}
                                      <Td><Badge $tone={e.cumple ? 'ok' : 'muted'}>{e.cumple ? 'Cumple' : 'Pendiente'}</Badge></Td>
                                    </tr>
                                  ))}
                                  {suyos.length === 0 && (
                                    <tr><Td colSpan={3 + mods.length + evs.length} style={{ textAlign: 'center' }}>
                                      Sin estudiantes de este perfil con los filtros actuales.
                                    </Td></tr>
                                  )}
                                </tbody>
                              </Table>
                            </TableWrap>
                            {suyos.length > VER_POR_CERT && (
                              <div style={{ fontSize: '0.78rem', color: tema.faint, padding: '8px 14px' }}>
                                Mostrando {VER_POR_CERT} de {suyos.length}. Descargue el CSV para verlos todos.
                              </div>
                            )}
                          </>
                        )}
                      </Panel>
                    );
                  })
                : (modMeta.length > 0 || evalMeta.length > 0) && (
                  <Panel>
                    <PanelHead style={{ cursor: 'pointer' }} onClick={() => alternar('prog')}>
                      <h3>{abiertas.prog ? '▾' : '▸'} Progreso por estudiante</h3>
                      <p>{visibles.filter(e => e.cumple).length} de {visibles.length} certificados · detalle módulo a módulo</p>
                    </PanelHead>
                    {abiertas.prog && (
                      <TableWrap>
                        <Table>
                          <thead>
                            <tr>
                              <Th>Estudiante</Th><Th>Correo</Th>
                              {modMeta.map(x => <Th key={`m${x.documento_id}`}>{x.titulo}</Th>)}
                              {evalMeta.map(x => <Th key={`e${x.documento_id}`}>Eval: {x.titulo}</Th>)}
                              <Th>Certificación</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {paged.map((e, i) => (
                              <tr key={i}>
                                <Td style={{ color: tema.text, fontWeight: 600 }}>{e.nombre}</Td>
                                <Td>{e.email}</Td>
                                {modMeta.map(x => {
                                  const md = e.mod?.[String(x.documento_id)] || { pct: 0 };
                                  return <Td key={`m${x.documento_id}`} style={{ color: tonoPorPct(tema, md.pct), fontWeight: 700 }}>{md.pct}%</Td>;
                                })}
                                {evalMeta.map(x => {
                                  const ev = e.eval?.[String(x.documento_id)];
                                  return (
                                    <Td key={`e${x.documento_id}`} style={{ fontWeight: 700, color: ev ? (ev.aprobado ? tema.ok : tonoPorPct(tema, ev.pct)) : tema.faint }}>
                                      {ev ? `${ev.pct}%${ev.aprobado ? ' ✓' : ''}` : '—'}
                                    </Td>
                                  );
                                })}
                                <Td><Badge $tone={e.cumple ? 'ok' : 'muted'}>{e.cumple ? 'Cumple' : 'Pendiente'}</Badge></Td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableWrap>
                    )}
                  </Panel>
                )}
            </>
          )}

          {/* ══ CERTIFICACIÓN DEL EVENTO (plataforma de eventos) ══ */}
          {tipoActual === 'evento' && data.evento && (
            <EventoCertificacion evento={data.evento} tema={tema} nombreArchivo={nombreArchivo} paisGlobal={f.pais} />
          )}

          {/* ══ CONSULTAS AL ASISTENTE ══ */}
          {tipoActual === 'consultas' && (
            consultas ? (() => {
              const cs = consultas;
              const maxTema = Math.max(1, ...cs.temas.map(t => t.value));
              const maxDoc = Math.max(1, ...cs.documentos.map(t => t.value));
              const maxMes = Math.max(1, ...cs.meses.map(t => t.value));
              const visibles = cs.lista.filter(p => (!temaConsulta || (p.tema || 'Sin clasificar') === temaConsulta)
                && (!modoConsulta || p.modo === modoConsulta));
              return (
                <>
                  <Heading>
                    <div><Eyebrow>Consultas al asistente</Eyebrow><H2>Qué le preguntan a la IA del curso</H2></div>
                    <Actions><Btn onClick={exportarConsultas}>⬇ Descargar preguntas</Btn></Actions>
                  </Heading>
                  <Note style={{ marginBottom: 12, maxWidth: 'none' }}>
                    {cs.filtrando
                      ? `${nf.format(cs.total)} de ${nf.format(cs.totalGeneral)} preguntas, según los filtros.`
                      : `${nf.format(cs.total)} preguntas.`} El tema de cada pregunta lo asigna la IA. Las descargas respetan los filtros.
                  </Note>

                  <KpiGrid>
                    <KpiCard $tone={tema.serieA}>
                      <span>Preguntas</span><strong>{nf.format(cs.total)}</strong>
                      <small>{cs.meses.length ? `desde ${cs.meses[0].name}` : ''}</small>
                    </KpiCard>
                    <KpiCard $tone={tema.serieC}>
                      <span>Estudiantes que preguntaron</span><strong>{nf.format(cs.personas)}</strong>
                      <small>{pct(cs.personas / Math.max(1, filtrados.length))} de los estudiantes</small>
                    </KpiCard>
                    <KpiCard $tone={tema.ok}>
                      <span>Preguntas por estudiante</span><strong>{df.format(cs.total / Math.max(1, cs.personas))}</strong>
                      <small>entre quienes preguntaron</small>
                    </KpiCard>
                    <KpiCard $tone={tema.warn}>
                      <span>Tema más frecuente</span>
                      <strong style={{ fontSize: '1.1rem' }}>{cs.utiles[0]?.name || '—'}</strong>
                      <small>{cs.utiles[0] ? `${nf.format(cs.utiles[0].value)} preguntas` : 'Sin clasificar'}</small>
                    </KpiCard>
                  </KpiGrid>

                  <Grid2 style={{ marginBottom: 14 }}>
                    <Panel style={{ marginBottom: 0 }}>
                      <PanelHead><h3>Temas de las preguntas</h3><p>Pulsa un tema para leer sus preguntas.</p></PanelHead>
                      <PanelBody>
                        <Bars>
                          {cs.temas.map(t => {
                            const on = temaConsulta === t.name;
                            return (
                              <BarBtn key={t.name} $on={on} aria-pressed={on}
                                      onClick={() => { setConsultasMax(25); setTemaConsulta(on ? null : t.name); }}>
                                <span title={t.name}>{t.name}</span>
                                <Track><FillA style={{ width: `${(t.value / maxTema) * 100}%`, background: tema.serieC }} /></Track>
                                <BarVal>{nf.format(t.value)}<em>{pct(t.value / Math.max(1, cs.total))}</em></BarVal>
                              </BarBtn>
                            );
                          })}
                        </Bars>
                      </PanelBody>
                    </Panel>
                    <Panel style={{ marginBottom: 0 }}>
                      <PanelHead><h3>Desde qué contenido preguntan</h3></PanelHead>
                      <PanelBody>
                        <Bars>
                          {cs.documentos.slice(0, 12).map(d => (
                            <BarRow key={d.name}>
                              <span title={d.name}>{d.name}</span>
                              <Track><FillA style={{ width: `${(d.value / maxDoc) * 100}%` }} /></Track>
                              <BarVal>{nf.format(d.value)}</BarVal>
                            </BarRow>
                          ))}
                        </Bars>
                      </PanelBody>
                    </Panel>
                  </Grid2>

                  {cs.meses.length > 1 && (
                    <Panel>
                      <PanelHead><h3>Preguntas por mes</h3></PanelHead>
                      <PanelBody>
                        <Bars>
                          {cs.meses.map(m => (
                            <BarRow key={m.name}>
                              <span>{m.name}</span>
                              <Track><FillA style={{ width: `${(m.value / maxMes) * 100}%`, background: tema.serieB }} /></Track>
                              <BarVal>{nf.format(m.value)}</BarVal>
                            </BarRow>
                          ))}
                        </Bars>
                      </PanelBody>
                    </Panel>
                  )}

                  <Panel>
                    <PanelHead>
                      <h3>Preguntas</h3>
                      <p>
                        {nf.format(visibles.length)} preguntas{temaConsulta ? ` · ${temaConsulta}` : ''}{modoConsulta ? ` · ${modoConsulta === 'mentor' ? 'modo mentor' : 'modo consulta'}` : ''}.
                        {(temaConsulta || modoConsulta) && (
                          <Ghost style={{ padding: '2px 8px', fontSize: '0.74rem', marginLeft: 8 }}
                                 onClick={() => { setTemaConsulta(null); setModoConsulta(''); }}>Ver todas</Ghost>
                        )}
                      </p>
                    </PanelHead>
                    <PanelBody style={{ paddingTop: 6 }}>
                      <Chips role="group" aria-label="Modo" style={{ marginBottom: 10 }}>
                        {[['', 'Todos'], ['consulta', 'Modo consulta'], ['mentor', 'Modo mentor']].map(([v, t]) => (
                          <Chip key={v} $active={modoConsulta === v} onClick={() => { setConsultasMax(25); setModoConsulta(v); }}>{t}</Chip>
                        ))}
                      </Chips>
                      {visibles.slice(0, consultasMax).map((p, i) => (
                        <Comentario key={`${p.fecha}_${i}`}>
                          <p>{p.texto}</p>
                          <small>
                            {p.nombre} · {fmtFecha(p.fecha)}{p.documento ? ` · ${p.documento}` : ''}
                            {p.tema ? ` · ${p.tema}` : ''}{p.modo === 'mentor' ? ' · modo mentor' : ''}
                          </small>
                        </Comentario>
                      ))}
                      {visibles.length > consultasMax && (
                        <Ghost style={{ marginTop: 10 }} onClick={() => setConsultasMax(n => n + 50)}>
                          Ver más ({nf.format(visibles.length - consultasMax)} restantes)
                        </Ghost>
                      )}
                      {visibles.length === 0 && <Empty>Ninguna pregunta con ese filtro.</Empty>}
                    </PanelBody>
                  </Panel>
                </>
              );
            })() : (
              <>
                <Heading><div><Eyebrow>Consultas al asistente</Eyebrow><H2>Todavía sin preguntas</H2></div></Heading>
                <Empty>Los estudiantes de este programa aún no le han preguntado nada al asistente de IA.</Empty>
              </>
            )
          )}

          {/* ══ DEMOGRAFÍA ══ */}
          {tipoActual === 'demografico' && demografia && (() => {
            const dm = demografia;
            const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
            const ORDEN = [1, 2, 3, 4, 5, 6, 0];
            const hora = (h) => (h === null ? '—' : `${h}:00–${h + 1}:00`);
            const maxFranja = Math.max(1, ...dm.franjas);
            const maxHora = Math.max(1, ...dm.horas);
            const maxDia = Math.max(1, ...dm.dias);
            const segP = dm.segmentos[0];
            const distintos = segP ? segP.filas.filter(f => f.name !== SIN_DATO && f.name !== 'Sin dato').length : 0;
            const n = filtrados.length;
            return (
              <>
                <Heading>
                  <div><Eyebrow>Demografía y actividad</Eyebrow><H2>Quiénes son y cuándo se conectan</H2></div>
                  <Actions>
                    <Ghost onClick={exportarDemografiaResumen}>⬇ Descargar resumen por {(segP?.etiqueta || 'segmento').toLowerCase()}</Ghost>
                    <Btn onClick={exportarDemografiaEstudiantes}>⬇ Descargar por estudiante</Btn>
                  </Actions>
                </Heading>
                <Note style={{ marginBottom: 12, maxWidth: 'none' }}>
                  La hora es la local de cada estudiante según su país. La actividad cuenta avances en videos, evaluaciones y consultas;
                  no incluye datos migrados de otras plataformas. Las descargas respetan los filtros.
                </Note>

                <KpiGrid>
                  <KpiCard $tone={tema.serieA}><span>Estudiantes</span><strong>{nf.format(n)}</strong><small>{nf.format(dm.conActividad)} con actividad registrada</small></KpiCard>
                  {segP && (
                    <KpiCard $tone={tema.serieC}>
                      <span>{segP.etiqueta}: distintos</span><strong>{nf.format(distintos)}</strong>
                      <small>{segP.filas[0] ? `Más estudiantes: ${segP.filas[0].name}` : '—'}</small>
                    </KpiCard>
                  )}
                  <KpiCard $tone={tema.ok}>
                    <span>Hora de mayor actividad</span><strong>{dm.horaPico === null ? '—' : `${dm.horaPico} h`}</strong>
                    <small>{dm.horaPico === null ? 'Sin actividad' : `${hora(dm.horaPico)} · ${pct(dm.horas[dm.horaPico] / Math.max(1, dm.total))} de la actividad`}</small>
                  </KpiCard>
                  <KpiCard $tone={tema.warn}>
                    <span>Día de mayor actividad</span><strong style={{ textTransform: 'capitalize' }}>{dm.diaPico === null ? '—' : DIAS[dm.diaPico]}</strong>
                    <small>{dm.diaPico === null ? 'Sin actividad' : `${pct(dm.dias[dm.diaPico] / Math.max(1, dm.total))} de la actividad`}</small>
                  </KpiCard>
                </KpiGrid>

                {dm.total > 0 ? (
                  <>
                    <Panel>
                      <PanelHead>
                        <h3>Cuándo se conectan</h3>
                        <p>Cada casilla es una hora de un día de la semana; más oscura, más actividad.</p>
                      </PanelHead>
                      <PanelBody style={{ overflowX: 'auto' }}>
                        <Calor role="img" aria-label={`Actividad por día y hora. Mayor actividad: ${dm.diaPico !== null ? DIAS[dm.diaPico] : ''} y ${hora(dm.horaPico)}`}>
                          <span />
                          {Array.from({ length: 24 }, (_, h) => <span key={h} className="hora">{h % 3 === 0 ? h : ''}</span>)}
                          {ORDEN.map(d => (
                            <React.Fragment key={d}>
                              <span className="dia" style={{ textTransform: 'capitalize' }}>{DIAS[d]}</span>
                              {Array.from({ length: 24 }, (_, h) => {
                                const v = dm.franjas[d * 24 + h];
                                return (
                                  <i key={h} title={`${DIAS[d]} ${hora(h)}: ${nf.format(v)} actividades`}>
                                    {v > 0 && <b style={{ opacity: 0.15 + 0.85 * (v / maxFranja) }} />}
                                  </i>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </Calor>
                      </PanelBody>
                    </Panel>

                    <Grid2 style={{ marginBottom: 14 }}>
                      <Panel style={{ marginBottom: 0 }}>
                        <PanelHead><h3>Por hora del día</h3><p>La barra verde es la hora de mayor actividad.</p></PanelHead>
                        <PanelBody>
                          <BarrasHora role="img" aria-label={`Hora de mayor actividad: ${hora(dm.horaPico)}`}>
                            {dm.horas.map((v, h) => (
                              <div key={h} title={`${hora(h)}: ${nf.format(v)} actividades`}>
                                <i className={h === dm.horaPico ? 'pico' : undefined} style={{ height: `${(v / maxHora) * 100}%` }} />
                              </div>
                            ))}
                          </BarrasHora>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', fontSize: '0.66rem', color: tema.muted, marginTop: 4 }}>
                            {dm.horas.map((_, h) => <span key={h} style={{ textAlign: 'center' }}>{h % 3 === 0 ? `${h}h` : ''}</span>)}
                          </div>
                        </PanelBody>
                      </Panel>
                      <Panel style={{ marginBottom: 0 }}>
                        <PanelHead><h3>Por día de la semana</h3></PanelHead>
                        <PanelBody>
                          <Bars>
                            {ORDEN.map(d => (
                              <BarRow key={d}>
                                <span style={{ textTransform: 'capitalize' }}>{DIAS[d]}</span>
                                <Track><FillA style={{ width: `${(dm.dias[d] / maxDia) * 100}%`, background: d === dm.diaPico ? tema.serieB : undefined }} /></Track>
                                <BarVal>{nf.format(dm.dias[d])}<em>{pct(dm.dias[d] / Math.max(1, dm.total))}</em></BarVal>
                              </BarRow>
                            ))}
                          </Bars>
                        </PanelBody>
                      </Panel>
                    </Grid2>
                  </>
                ) : (
                  <Empty style={{ marginBottom: 14 }}>Los estudiantes filtrados todavía no tienen actividad registrada en la plataforma.</Empty>
                )}

                {dm.segmentos.map(sg => {
                  const max = Math.max(1, ...sg.filas.slice(0, 20).map(f => f.registrados));
                  return (
                    <Panel key={sg.campo}>
                      <PanelHead>
                        <h3>Estudiantes por {sg.etiqueta.charAt(0).toLowerCase() + sg.etiqueta.slice(1)}</h3>
                        <p>{nf.format(sg.filas.length)} valores distintos{sg.filas.length > 20 ? ' · se muestran los 20 con más estudiantes' : ''}.</p>
                      </PanelHead>
                      <PanelBody>
                        <Bars>
                          {sg.filas.slice(0, 20).map(f => (
                            <BarRow key={f.name}>
                              <span title={f.name}>{f.name}</span>
                              <Track><FillA style={{ width: `${(f.registrados / max) * 100}%` }} /></Track>
                              <BarVal>{nf.format(f.registrados)}<em>{pct(f.registrados / Math.max(1, n))}</em></BarVal>
                            </BarRow>
                          ))}
                        </Bars>
                      </PanelBody>
                    </Panel>
                  );
                })}

                {segP && dm.total > 0 && (
                  <Panel>
                    <PanelHead>
                      <h3>Actividad por {segP.etiqueta.charAt(0).toLowerCase() + segP.etiqueta.slice(1)}</h3>
                      <p>Cuántos tienen actividad y cuándo se conectan en cada {segP.etiqueta.toLowerCase()}.</p>
                    </PanelHead>
                    <TableWrap>
                      <Table>
                        <thead>
                          <tr><Th>{segP.etiqueta}</Th><Th>Estudiantes</Th><Th>Con actividad</Th><Th>Actividades</Th><Th>Hora de más actividad</Th><Th>Día de más actividad</Th></tr>
                        </thead>
                        <tbody>
                          {dm.tabla.slice(0, 25).map(f => (
                            <tr key={f.name}>
                              <Td style={{ color: tema.text, fontWeight: 600 }}>{f.name}</Td>
                              <Td>{nf.format(f.estudiantes)}</Td>
                              <Td>{nf.format(f.activos)} <span style={{ color: tema.muted }}>({pct(f.activos / Math.max(1, f.estudiantes))})</span></Td>
                              <Td>{nf.format(f.actividad)}</Td>
                              <Td>{f.horaPico === null ? '—' : hora(f.horaPico)}</Td>
                              <Td style={{ textTransform: 'capitalize' }}>{f.diaPico === null ? '—' : DIAS[f.diaPico]}</Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  </Panel>
                )}
              </>
            );
          })()}

          {/* ══ CALIDAD DE DATOS ══ */}
          {tipoActual === 'calidad' && (
            <>
              <Heading>
                <div><Eyebrow>Calidad de datos</Eyebrow><H2>Registros que requieren revisión</H2></div>
                <Actions><Btn onClick={exportarIncidencias}>⬇ Descargar registros con problemas</Btn></Actions>
              </Heading>

              <AuditHero>
                <div>
                  <span>Cuentas registradas</span><strong>{nf.format(calidad.cuentasFuente)}</strong>
                  {calidad.excluidas > 0 && <small>{nf.format(calidad.excluidas)} de prueba o internas, fuera de los datos</small>}
                </div>
                <div>
                  <span>Profesionales únicos</span><strong>{nf.format(calidad.unicos)}</strong>
                  <small>Por número de identificación</small>
                </div>
                <div>
                  <span>Estudiantes analizados</span><strong>{nf.format(filtrados.length)}</strong>
                  <small>{filtrados.length === (data?.estudiantes || []).length ? 'Sin filtros' : 'Con los filtros actuales'}</small>
                </div>
              </AuditHero>

              <KpiGrid>
                <KpiCard $tone={tema.serieA}>
                  <span>Grupos duplicados</span><strong>{nf.format(calidad.grupos)}</strong><small>Identificaciones compartidas</small>
                </KpiCard>
                <KpiCard $tone={tema.serieC}>
                  <span>Cuentas por descartar</span><strong>{nf.format(calidad.descartables)}</strong><small>Se sugiere conservar la de mayor avance</small>
                </KpiCard>
                {calidad.sinRuta !== null && (
                  <KpiCard $tone={tema.warn}>
                    <span>Sin ruta de certificación</span><strong>{nf.format(calidad.sinRuta)}</strong><small>Perfil sin certificado asignado</small>
                  </KpiCard>
                )}
                {calidad.kpiAsign.map(a => (
                  <KpiCard key={a.campo} $tone={tema.bad}>
                    <span>{a.por_defecto ? `${a.etiqueta}: ${a.por_defecto}` : `Sin ${a.etiqueta.toLowerCase()}`}</span>
                    <strong>{nf.format(a.n)}</strong><small>Asignación por confirmar</small>
                  </KpiCard>
                ))}
                <KpiCard $tone={tema.lime}>
                  <span>Con incidencias</span><strong>{nf.format(calidad.conProblema)}</strong>
                  <small>{nf.format(calidad.incidencias.length)} en total</small>
                </KpiCard>
              </KpiGrid>

              <Grid2 style={{ marginBottom: 14 }}>
                <Panel style={{ marginBottom: 0 }}>
                  <PanelHead><h3>Incidencias por tipo</h3></PanelHead>
                  <PanelBody><BarrasSimples rows={calidad.tipos} limite={15} color={tema.serieC} /></PanelBody>
                </Panel>
                <Panel style={{ marginBottom: 0 }}>
                  <PanelHead><h3>Criterios de conciliación</h3></PanelHead>
                  <PanelBody>
                    <MiniGrid>
                      <Mini><span>1</span><strong>Número de identificación</strong><p>Sin puntos, espacios ni guiones. Principal criterio de cruce.</p></Mini>
                      <Mini><span>2</span><strong>Correo</strong><p>Cada cuenta es un correo; dos correos con la misma identificación son la misma persona.</p></Mini>
                      <Mini><span>3</span><strong>Mayor avance</strong><p>Cuenta sugerida para conservar; si empatan, la de actividad más reciente.</p></Mini>
                    </MiniGrid>
                  </PanelBody>
                </Panel>
              </Grid2>

              <Panel>
                <PanelHead>
                  <h3>Cuentas duplicadas</h3>
                  <p>{calidad.grupos ? `${nf.format(calidad.grupos)} identificaciones repetidas en ${nf.format(calidad.duplicadas.length)} cuentas.` : 'No hay identificaciones repetidas.'}</p>
                </PanelHead>
                {calidad.duplicadas.length > 0 && (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr><Th>Grupo</Th><Th>Nombre</Th><Th>Identificación</Th><Th>Correo</Th><Th>Avance</Th><Th>Última actividad</Th><Th>Decisión</Th></tr>
                      </thead>
                      <tbody>
                        {calidad.duplicadas.map((d, k) => (
                          <tr key={k} style={k > 0 && calidad.duplicadas[k - 1].grupo !== d.grupo ? { borderTop: `2px solid ${tema.line}` } : undefined}>
                            <Td>{d.grupo}</Td>
                            <Td style={{ color: tema.text, fontWeight: 600 }}>{d.e.nombre}</Td>
                            <Td>{d.e.dni}</Td>
                            <Td>{d.e.email}</Td>
                            <Td style={{ color: tonoPorPct(tema, d.e.avance || 0), fontWeight: 700 }}>{d.e.avance ?? 0}%</Td>
                            <Td>{d.e.ultima_actividad ? fmtFecha(d.e.ultima_actividad) : 'Nunca'}</Td>
                            <Td><Badge $tone={d.conservar ? 'ok' : 'warn'}>{d.conservar ? 'Conservar' : 'Revisar para descartar'}</Badge></Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </Panel>

              <Panel>
                <PanelHead>
                  <h3>Registros con problemas</h3>
                  <p>{nf.format(calidad.incidencias.length)} incidencias{calidad.incidencias.length > 300 ? ' · se muestran las primeras 300; la descarga trae todas' : ''}.</p>
                </PanelHead>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr><Th>Tipo</Th><Th>Severidad</Th><Th>Acción sugerida</Th><Th>Estudiante</Th><Th>Correo</Th><Th>Documento</Th><Th>Estado</Th><Th>Avance</Th></tr>
                    </thead>
                    <tbody>
                      {calidad.incidencias.slice(0, 300).map((i, k) => (
                        <tr key={k}>
                          <Td>{i.tipo}</Td>
                          <Td><Badge $tone={i.severidad === 'Alta' ? 'bad' : i.severidad === 'Media' || i.severidad === 'Operativa' ? 'warn' : undefined}>{i.severidad}</Badge></Td>
                          <Td>{i.accion}</Td>
                          <Td style={{ color: tema.text, fontWeight: 600 }}>{i.nombre}</Td>
                          <Td>{i.email}</Td>
                          <Td>{i.dni || '—'}</Td>
                          <Td>{i.estado}</Td>
                          <Td style={{ color: tonoPorPct(tema, i.avance || 0), fontWeight: 700 }}>{i.avance ?? 0}%</Td>
                        </tr>
                      ))}
                      {calidad.incidencias.length === 0 && (
                        <tr><Td colSpan={8} style={{ textAlign: 'center' }}>Sin incidencias: los datos están completos.</Td></tr>
                      )}
                    </tbody>
                  </Table>
                </TableWrap>
              </Panel>
            </>
          )}

          {/* ══ COMUNICACIONES ══ */}
          {tipoActual === 'comunicaciones' && (
            <>
              <Heading>
                <div><Eyebrow>Comunicaciones</Eyebrow><H2>Correos enviados a estos estudiantes</H2></div>
                {correos.hay && <Actions><Btn onClick={exportarCorreos}>⬇ Descargar la lista</Btn></Actions>}
              </Heading>

              {correos.hayAlguno && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
                  <Chips role="group" aria-label="Tipo de correo">
                    {TIPOS_CORREO.map(t => {
                      const n = t.key ? todosMensajes.filter(m => m.tipo === t.key).length : todosMensajes.length;
                      if (t.key && !n) return null;
                      return (
                        <Chip key={t.key || 'todos'} $active={tipoCorreo === t.key} aria-pressed={tipoCorreo === t.key}
                              onClick={() => { setTipoCorreo(t.key); setCampana(''); }}>
                          {t.label}
                        </Chip>
                      );
                    })}
                  </Chips>
                  {tipoCorreo === 'comunicado' && campanas.length > 1 && (
                    <select value={campana} onChange={e => setCampana(e.target.value)} aria-label="Campaña"
                            style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${tema.line}`, background: tema.card, color: tema.text, font: 'inherit', fontSize: '0.9em', maxWidth: 360 }}>
                      <option value="">Todas las campañas ({campanas.length})</option>
                      {campanas.map(c => (
                        <option key={c.key} value={c.key}>{c.titulo}{c.fecha ? ` · ${fmtFecha(c.fecha)}` : ''}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {!correos.hay ? (
                <Panel><PanelBody>
                  <Empty>
                    {correos.hayAlguno
                      ? 'No hay correos de ese tipo para los estudiantes de este tablero.'
                      : 'Todavía no se han enviado comunicados ni correos automáticos a los estudiantes de este tablero.'}
                  </Empty>
                </PanelBody></Panel>
              ) : (
                <>
                  <KpiGrid>
                    <KpiCard $tone={tema.serieA}>
                      <span>Correos enviados</span><strong>{nf.format(correos.tot.enviados)}</strong>
                      <small>A {nf.format(correos.tot.personas)} estudiantes</small>
                    </KpiCard>
                    <KpiCard $tone={tema.ok}>
                      <span>Entregados</span><strong>{nf.format(correos.tot.entregados)}</strong>
                      <small>{pct(correos.tot.enviados ? correos.tot.entregados / correos.tot.enviados : 0, 1)} de lo enviado</small>
                    </KpiCard>
                    <KpiCard $tone={tema.serieC}>
                      <span>Abiertos</span><strong>{nf.format(correos.tot.abrieron)}</strong>
                      <small>{pct(correos.tot.entregados ? correos.tot.abrieron / correos.tot.entregados : 0, 1)} de lo entregado</small>
                    </KpiCard>
                    <KpiCard $tone={tema.lime}>
                      <span>Con clic</span><strong>{nf.format(correos.tot.clic)}</strong>
                      <small>{pct(correos.tot.abrieron ? correos.tot.clic / correos.tot.abrieron : 0, 1)} de lo abierto</small>
                    </KpiCard>
                    <KpiCard $tone={tema.warn}>
                      <span>Rebotes</span><strong>{nf.format(correos.tot.rebotes)}</strong><small>Correo que no existe o no recibe</small>
                    </KpiCard>
                    <KpiCard $tone={tema.bad}>
                      <span>Reclamos y bajas</span><strong>{nf.format(correos.tot.quejas + correos.tot.bajas)}</strong>
                      <small>{nf.format(correos.tot.quejas)} spam · {nf.format(correos.tot.bajas)} bajas</small>
                    </KpiCard>
                  </KpiGrid>

                  <Panel>
                    <PanelHead>
                      <h3>Por mensaje</h3>
                      <p>Cada comunicado por separado, el correo de registro, cada correo del flujo automático en el orden del recorrido y los de restablecer contraseña.</p>
                    </PanelHead>
                    <TableWrap>
                      <Table>
                        <thead>
                          <tr><Th>Mensaje</Th><Th>Fecha</Th><Th>Enviados</Th><Th>Entregados</Th><Th>Abiertos</Th>
                            <Th>Clic</Th><Th>Rebotes</Th><Th>Reclamos</Th><Th>Bajas</Th></tr>
                        </thead>
                        <tbody>
                          {correos.mensajes.map(m => (
                            <tr key={m.key}>
                              <Td style={{ color: tema.text, fontWeight: 600 }}>
                                {m.titulo}
                                {m.grupo && m.tipo !== 'comunicado' && (
                                  <div style={{ fontWeight: 400, fontSize: '0.78em', opacity: 0.7, marginTop: 2 }}>{m.grupo}</div>
                                )}
                              </Td>
                              <Td>{m.fecha ? fmtFecha(m.fecha) : '—'}</Td>
                              <Td>{nf.format(m.enviados)}</Td>
                              <Td>{nf.format(m.entregados)}</Td>
                              <Td>{nf.format(m.abrieron)} <small style={{ opacity: 0.7 }}>{m.entregados ? pct(m.abrieron / m.entregados) : ''}</small></Td>
                              <Td>{nf.format(m.clic)}</Td>
                              <Td>{nf.format(m.rebotes)}</Td>
                              <Td>{nf.format(m.quejas)}</Td>
                              <Td>{nf.format(m.bajas)}</Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  </Panel>

                  <Panel>
                    <PanelHead>
                      <h3>¿Quién?</h3>
                      <p>Elige un grupo para ver las personas; la lista respeta los filtros de arriba.</p>
                    </PanelHead>
                    <PanelBody>
                      <Chips style={{ marginBottom: 12 }}>
                        {LISTAS_CORREO.map(l => (
                          <Chip key={l.key} $active={listaCorreo === l.key} onClick={() => setListaCorreo(l.key)}>
                            {l.label} · {nf.format(correos.listas[l.key].length)}
                          </Chip>
                        ))}
                      </Chips>
                    </PanelBody>
                    <TableWrap>
                      <Table>
                        <thead><tr><Th>Estudiante</Th><Th>Correo</Th><Th>Estado en el programa</Th><Th>Mensajes</Th></tr></thead>
                        <tbody>
                          {correos.listas[listaCorreo].slice(0, 300).map((f, i) => (
                            <tr key={i}>
                              <Td style={{ color: tema.text, fontWeight: 600 }}>{f.nombre}</Td>
                              <Td>{f.email}</Td>
                              <Td>{f.estado}</Td>
                              <Td style={{ fontSize: '0.82em' }}>
                                {f.detalle.map((d, j) => (
                                  <div key={j}>
                                    {d.titulo}: {ETQ_CORREO[d.estado] || d.estado}{d.a ? ' · abrió' : ''}{d.k ? ' · clic' : ''}{d.b ? ' · baja' : ''}
                                  </div>
                                ))}
                              </Td>
                            </tr>
                          ))}
                          {correos.listas[listaCorreo].length === 0 && (
                            <tr><Td colSpan={4} style={{ textAlign: 'center' }}>Nadie en este grupo.</Td></tr>
                          )}
                        </tbody>
                      </Table>
                    </TableWrap>
                    {correos.listas[listaCorreo].length > 300 && (
                      <Note style={{ padding: '8px 16px' }}>Se muestran 300; descarga la lista para verlos todos.</Note>
                    )}
                  </Panel>

                  <Note style={{ marginTop: 10 }}>
                    Las aperturas se miden con una imagen invisible: quien tiene las imágenes bloqueadas
                    puede leer el correo sin que cuente como abierto.
                  </Note>
                </>
              )}
            </>
          )}
        </Container>

        <Foot>
          {data.nombre} · Dashboard ejecutivo de programa · Los filtros se aplican en tu navegador, sobre los datos del corte.
        </Foot>
      </Wrap>
    </ThemeProvider>
  );
};

export default DashboardPublicoPage;
